import React, { useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "../context/AuthContext";
import { useWorkspace } from "../context/WorkspaceContext";
import { useClasses } from "../context/ClassesContext";
import { useBg } from "../hooks/useBg";
import GoalsPanel from "./GoalsPanel";
import ActivityFeed from "./ActivityFeed";
import InboxPanel, { InboxBadge } from "./InboxPanel";
import { useFolders } from "../context/FoldersContext";
import { useTemplates } from "../context/TemplatesContext";
import { useKeyPairs } from "../context/KeyPairsContext";
import { useDocuments } from "../context/DocumentsContext";
import { useTags } from "../context/TagsContext";
import Logo, { MinimalLogo, BarsLogo } from "./Logo";
import { confirmDelete } from "./ConfirmDialog";
import { ThemedSelect } from "./ThemedSelect";
import { Plus, Users, Check, Trash2, CheckSquare, Repeat, Zap, Bell, PlayCircle, Settings as SettingsIcon, LogOut, UserPlus, Edit3, FileEdit, StickyNote, Folder as FolderIcon, FolderPlus, ChevronRight, ChevronDown, ChevronLeft, FileText, Globe, History, Key, Upload, Link as LinkIcon, Download, Sparkles, Crown, Type, Hash, Palette, Pilcrow, ToggleLeft, Plug, Inbox, Target, Activity, Building2, ArrowUp, BarChart2, Flag, GitBranch as GitBranchIcon, Compass as CompassIcon, Navigation as NavigationIcon, BookOpen, ZapOff, Anchor as AnchorIcon, LayoutDashboard, Archive, Layers } from "lucide-react";
import SpotifyNowPlaying from "./SpotifyNowPlaying";
import { BooksIcon } from "./BrandIcons";
import ConditionIcon from "./ConditionIcon";
import SequenceIcon from "./SequenceIcon";
import SidebarSearch from "./SidebarSearch";
import ProjectTree from "./ProjectTree";
import { Link } from "react-router-dom";
import { useEntitlements } from "../context/EntitlementsContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { toast } from "sonner";
import { NODE_TYPE_COLORS, NODE_TYPES, api } from "../lib/api";
import SettingsDialog from "./SettingsDialog";

const PRESET_COLORS = ["#0055FF", "#FF3B30", "#FFCC00", "#00C781", "#FF8A00", "#A855F7", "#EC4899", "#14B8A6"];
const TYPE_ICONS = { task: CheckSquare, routine: Repeat, action: Zap, reminder: Bell, trigger: PlayCircle, condition: ConditionIcon, sequence: SequenceIcon, note: StickyNote, resource: FileText };

// Iter35 user request: key-pair value type chooser became icons-only,
// nested inside the value input. Map data type → lucide icon.
const KP_TYPE_ICON = {
  string: Pilcrow,    // text/string — paragraph-style
  number: Hash,       // numeric
  color: Palette,     // color swatch
  font: Type,         // font family
  boolean: ToggleLeft,
};
const KP_TYPES = [
  { value: "string", label: "Text" },
  { value: "number", label: "Number" },
  { value: "color", label: "Color" },
  { value: "font", label: "Font" },
  { value: "boolean", label: "Boolean" },
];

// ─── Unscheduled Panel ────────────────────────────────────────────────────────
function UnscheduledPanel({ workspaceId }) {
  const [tasks, setTasks] = useState([]);
  const [collapsed, setCollapsed] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const load = async () => {
    if (!workspaceId) return;
    try {
      const { data } = await api.get(`/workspaces/${workspaceId}/unscheduled`);
      setTasks(data || []);
    } catch {}
  };

  useEffect(() => { load(); }, [workspaceId]);

  useEffect(() => {
    const fn = () => load();
    window.addEventListener("crescendo:unscheduled-changed", fn);
    window.addEventListener("crescendo:events-changed", fn);
    return () => {
      window.removeEventListener("crescendo:unscheduled-changed", fn);
      window.removeEventListener("crescendo:events-changed", fn);
    };
  }, [workspaceId]);

  const removeTask = async (id) => {
    try {
      await api.delete(`/workspaces/${workspaceId}/events/${id}`);
      setTasks(t => t.filter(x => x.event_id !== id));
    } catch {}
  };

  const onDragOver = (e) => {
    const types = e.dataTransfer.types;
    const hasEvent = types.includes("application/x-crescendo-event-id");
    const hasNodeType = types.includes("application/x-crescendo-node-type");
    if (hasEvent || hasNodeType) {
      e.preventDefault();
      // New nodes use "copy" effectAllowed; existing events use "move"
      e.dataTransfer.dropEffect = hasEvent ? "move" : "copy";
      setDragOver(true);
    }
  };
  const onDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false);
  };
  const onDrop = async (e) => {
    setDragOver(false);
    // Existing calendar node dropped → mark as unscheduled
    const eventId = e.dataTransfer.getData("application/x-crescendo-event-id");
    if (eventId && workspaceId) {
      e.preventDefault();
      try {
        await api.put(`/workspaces/${workspaceId}/events/${eventId}`, { unscheduled: true });
        window.dispatchEvent(new CustomEvent("crescendo:events-changed"));
        window.dispatchEvent(new CustomEvent("crescendo:unscheduled-changed"));
      } catch {}
      return;
    }
    // New node type dragged from the palette → open EventDialog in unscheduled mode
    const nodeType = e.dataTransfer.getData("application/x-crescendo-node-type");
    if (nodeType) {
      e.preventDefault();
      window.dispatchEvent(new CustomEvent("crescendo:add-unscheduled", { detail: { type: nodeType } }));
    }
  };

  return (
    <div
      className={`px-3 py-2 border-b border-[#2A2A2A] transition-colors ${dragOver ? "bg-[#5A90F5]/8 border-[#5A90F5]/30" : ""}`}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <div className="flex items-center justify-between mb-1.5">
        <button
          className="meta-label flex items-center gap-1 hover:text-[#EDEDED]"
          onClick={() => setCollapsed(c => !c)}
        >
          <ChevronDown className={`w-3 h-3 transition-transform ${collapsed ? "-rotate-90" : ""}`} />
          Unscheduled
        </button>
        <button
          onClick={() => {
            setCollapsed(false);
            window.dispatchEvent(new CustomEvent("crescendo:add-unscheduled"));
          }}
          className="p-1 rounded hover:bg-[#1a1a1a] text-[#A1A1AA] hover:text-[#EDEDED]"
          title="Add unscheduled task"
        >
          <Plus className="w-3.5 h-3.5" />
        </button>
      </div>

      {!collapsed && (
        <div className="space-y-0.5">
          {/* Drop zone — styled like the calendar's "add node" button */}
          <div
            className={`flex items-center gap-2 w-full px-2 py-1.5 rounded border border-dashed text-[11px] transition-colors cursor-default
              ${dragOver
                ? "border-[#5A90F5]/60 text-[#5A90F5] bg-[#5A90F5]/8"
                : "border-[#2A2A2A] text-[#3F3F46]"}`}
          >
            <Plus className="w-3 h-3 flex-shrink-0" />
            <span>Drop a node</span>
          </div>
          {tasks.map(t => (
            <div
              key={t.event_id}
              draggable
              onDragStart={e => {
                e.dataTransfer.setData("application/x-crescendo-unscheduled-id", t.event_id);
                e.dataTransfer.setData("application/x-crescendo-unscheduled-title", t.title);
                e.dataTransfer.effectAllowed = "move";
              }}
              onClick={() => window.dispatchEvent(new CustomEvent("crescendo:edit-unscheduled", { detail: { event: t } }))}
              className="group flex items-center gap-2 px-2 py-1 rounded cursor-pointer hover:bg-[#151515]"
              title="Click to edit or set time · Drag to calendar to schedule"
            >
              <Inbox className="w-3.5 h-3.5 text-[#52525B] flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <span className="text-xs truncate text-[#A1A1AA] block">{t.title}</span>
                {t.type && t.type !== "task" && (
                  <span className="text-[9px] text-[#52525B] capitalize">{t.type}</span>
                )}
              </div>
              <button
                onClick={async e => { e.stopPropagation(); await removeTask(t.event_id); }}
                className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-[#2a2a2a]"
              >
                <Trash2 className="w-3 h-3 text-[#FF3B30]" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}



// Dev-only: shows a full-screen loading overlay for 3s so devs can inspect it
function DevLoadingPreview() {
  const [showing, setShowing] = React.useState(false);
  const show = () => {
    setShowing(true);
    setTimeout(() => setShowing(false), 3000);
  };
  return (
    <>
      <button
        onClick={show}
        disabled={showing}
        className="w-full px-3 py-1.5 rounded border border-[#2A2A2A] text-[11px] text-[#A1A1AA] hover:text-[#EDEDED] hover:bg-[#1a1a1a] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {showing ? "Showing for 3s…" : "▶  Preview Loading Screen (3s)"}
      </button>
      {showing && (
        <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-[#0A0A0A]">
          <BarsLogo size={64} color="#EDEDED" animated />
        </div>
      )}
    </>
  );
}

class ActivityErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(err) { return { error: err }; }
  componentDidCatch() {}
  render() {
    if (this.state.error) {
      return (
        <div className="px-4 py-8 text-center">
          <p className="text-[11px] text-[#52525B]">Activity unavailable.</p>
          <button onClick={() => this.setState({ error: null })}
            className="mt-2 text-[10px] text-[#5A90F5] hover:underline">Retry</button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function Sidebar({ mainTab = "calendar", setMainTab = () => {}, collapsed = false, onToggleCollapse, autoCollapse = false, onToggleAutoCollapse, onAutoCollapseLeave }) {
  const { user, accounts, switchAccount, logout } = useAuth();
  const ent = useEntitlements();
  const { workspaces, current, setCurrent, createWorkspace, deleteWorkspace, reload: loadWorkspaces, setWorkspaces } = useWorkspace();
  const { classes, create, update, remove: removeCls, activeClassId, setActiveClassId, setClasses } = useClasses();
  const { folders, create: createFolder, update: updateFolder, remove: removeFolder } = useFolders();
  const { templates, save: saveTemplate, remove: removeTemplate } = useTemplates();
  // Template creation popup state.
  const [showNewTplDialog, setShowNewTplDialog] = useState(false);
  const [newTplName, setNewTplName] = useState("");
  const { pairs: keyPairs, create: createKp, remove: removeKp } = useKeyPairs();
  const { docs: allDocs, create: createDoc, update: updateDoc, remove: removeDoc, reload: reloadDocs, activeId: activeDocId, setActiveId: setActiveDocId } = useDocuments();
  const { tags, create: createTag, remove: removeTag } = useTags() || { tags: [] };
  // Tag inline-create state.
  const [newTagName, setNewTagName] = useState("");
  const refreshDocs = async () => {
    try { const { data } = await api.get("/documents"); setDocs(data || []); } catch {}
  };
  const [newKpKey, setNewKpKey] = useState("");
  const [newKpVal, setNewKpVal] = useState("");
  const [newKpType, setNewKpType] = useState("string");
  const kpKeyRef = useRef(null);

  // Per-class weekly time totals — fetched for the active workspace and
  // surfaced next to each class name. Lightweight: only summed from timed
  // events (task/routine). Refresh whenever events change.
  const [classTotals, setClassTotals] = useState({}); // { class_id: minutes }
  useEffect(() => {
    if (!current?.workspace_id) { setClassTotals({}); return; }
    const load = async () => {
      try {
        const { data } = await api.get(`/workspaces/${current.workspace_id}/events`);
        const totals = {};
        for (const ev of data || []) {
          if (!["task", "routine"].includes(ev.type)) continue;
          if (!ev.class_id) continue;
          const s = Date.parse(ev.start); const e = Date.parse(ev.end);
          if (Number.isNaN(s) || Number.isNaN(e) || e <= s) continue;
          totals[ev.class_id] = (totals[ev.class_id] || 0) + Math.round((e - s) / 60000);
        }
        setClassTotals(totals);
      } catch { /* ignore */ }
    };
    load();
    const onChange = () => load();
    window.addEventListener("crescendo:events-changed", onChange);
    const id = setInterval(load, 60000);
    return () => { window.removeEventListener("crescendo:events-changed", onChange); clearInterval(id); };
  }, [current?.workspace_id]);

  const fmtMinutes = (m) => {
    if (!m || m < 1) return "";
    if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60); const mm = m % 60;
    return mm ? `${h}h${mm}m` : `${h}h`;
  };

  // Reorder helpers — move a dragged row above OR below a target row, then renumber positions.
  // Optimistic: update local state immediately so the sidebar doesn't refresh/blink;
  // persist position changes to the backend in the background.
  const reorderWorkspaces = async (draggedId, targetId, dropAfter) => {
    if (draggedId === targetId) return;
    const list = [...workspaces].sort((a, b) => (a.position || 0) - (b.position || 0));
    const from = list.findIndex((w) => w.workspace_id === draggedId);
    if (from < 0) return;
    const [moved] = list.splice(from, 1);
    let idx = list.findIndex((w) => w.workspace_id === targetId);
    if (idx < 0) return;
    if (dropAfter) idx += 1;
    list.splice(idx, 0, moved);
    const renumbered = list.map((w, i) => ({ ...w, position: i }));
    setWorkspaces?.(renumbered);
    // Fire-and-forget persistence (no reload, no blink).
    renumbered.forEach((w, i) => { api.put(`/workspaces/${w.workspace_id}`, { position: i }).catch(() => {}); });
  };
  const reorderClasses = async (draggedId, targetId, dropAfter) => {
    if (draggedId === targetId) return;
    const list = [...classes].sort((a, b) => (a.position || 0) - (b.position || 0));
    const from = list.findIndex((c) => c.class_id === draggedId);
    if (from < 0) return;
    const [moved] = list.splice(from, 1);
    let idx = list.findIndex((c) => c.class_id === targetId);
    if (idx < 0) return;
    if (dropAfter) idx += 1;
    list.splice(idx, 0, moved);
    const renumbered = list.map((c, i) => ({ ...c, position: i }));
    setClasses?.(renumbered);
    renumbered.forEach((c, i) => { update(c.class_id, { position: i }).catch?.(() => {}); });
  };

  // Create a folder + immediately enter inline rename mode (like workspace/class rename).
  // Iter35 fix: accept optional `parentFolderId` so "New folder" inside a
  // sub-tab popover or right-click menu actually NESTS under the
  // parent (recurring user complaint: "Adding a new folder seems to
  // add it outside of the document, not underneath it").
  const addFolderAndRename = async (kind, parentFolderId = null) => {
    const folder = await createFolder(kind, parentFolderId);
    setExpandedFolders((s) => {
      const n = new Set(s);
      n.add(folder.folder_id);
      if (parentFolderId) n.add(parentFolderId);
      return n;
    });
    // Reload docs so the new folder appears in the tree immediately
    reloadDocs?.();
    setRenameTarget({ kind: "folder", id: folder.folder_id, value: folder.name });
    setTimeout(() => { renameInputRef.current?.focus(); renameInputRef.current?.select(); }, 50);
  };
  const [expandedFolders, setExpandedFolders] = useState(() => new Set());
  const toggleFolder = (id) => setExpandedFolders((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  // Multi-select state for grouping (Ctrl+G). Users shift-click items to add to selection.
  const [selectedWsIds, setSelectedWsIds] = useState(() => new Set());
  const [selectedClsIds, setSelectedClsIds] = useState(() => new Set());
  const [selectedTplIds, setSelectedTplIds] = useState(() => new Set());
  // Collapsible section state (issue #12). Persisted in localStorage so the
  // layout survives reloads.
  const [collapsedSections, setCollapsedSections] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("crescendo:collapsed-sections") || "[]")); }
    catch { return new Set(); }
  });
  const toggleSection = (key) => {
    setCollapsedSections((prev) => {
      const n = new Set(prev);
      n.has(key) ? n.delete(key) : n.add(key);
      try { localStorage.setItem("crescendo:collapsed-sections", JSON.stringify([...n])); } catch { /* noop */ }
      return n;
    });
  };
  const isCollapsed = (key) => collapsedSections.has(key);

  // Iter39.3: when the Spotify widget expands, collapse every other
  // sidebar section so the giant album art doesn't squeeze out the
  // user's view of their workspaces / classes / etc. The user can
  // still re-open them manually.
  useEffect(() => {
    const onSpotifyExpanded = () => {
      const SECTIONS = ["workspaces", "classes", "key-pairs", "templates", "spotlights"];
      setCollapsedSections((prev) => {
        const n = new Set(prev);
        for (const k of SECTIONS) n.add(k);
        try { localStorage.setItem("crescendo:collapsed-sections", JSON.stringify([...n])); } catch { /* noop */ }
        return n;
      });
    };
    window.addEventListener("crescendo:spotify-expanded", onSpotifyExpanded);
    return () => window.removeEventListener("crescendo:spotify-expanded", onSpotifyExpanded);
  }, []);
  // Keep refs in sync so the Ctrl+G handler never sees stale state.
  const selectedWsIdsRef = useRef(selectedWsIds);
  const selectedClsIdsRef = useRef(selectedClsIds);
  useEffect(() => { selectedWsIdsRef.current = selectedWsIds; }, [selectedWsIds]);
  useEffect(() => { selectedClsIdsRef.current = selectedClsIds; }, [selectedClsIds]);

  // Ctrl/Cmd+G → create folder and nest currently-selected items.
  // Optimistic: update local state immediately, persist in parallel in the
  // background. This fixes the old bug where one item would land in the folder
  // and the rest would appear outside until a full reload.
  useEffect(() => {
    const onKey = async (e) => {
      const isG = (e.ctrlKey || e.metaKey) && (e.key === "g" || e.key === "G");
      if (!isG) return;
      const tag = document.activeElement?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      const wsIds = [...selectedWsIdsRef.current];
      const clsIds = [...selectedClsIdsRef.current];
      if (wsIds.length === 0 && clsIds.length === 0) return;
      e.preventDefault();
      if (wsIds.length > 0) {
        const f = await createFolder("workspace", "New folder");
        // Optimistic: move every item locally *now*.
        setWorkspaces?.((prev) => prev.map((w) => wsIds.includes(w.workspace_id) ? { ...w, parent_folder_id: f.folder_id } : w));
        // Persist in parallel — don't block UI on network.
        await Promise.allSettled(wsIds.map((id) => api.put(`/workspaces/${id}`, { parent_folder_id: f.folder_id })));
        setExpandedFolders((s) => { const n = new Set(s); n.add(f.folder_id); return n; });
        setRenameTarget({ kind: "folder", id: f.folder_id, value: f.name });
        setTimeout(() => { renameInputRef.current?.focus(); renameInputRef.current?.select(); }, 50);
        setSelectedWsIds(new Set());
        /* silent */
      } else {
        const f = await createFolder("class", "New folder");
        // Optimistic local update first, then persist.
        setClasses?.((prev) => prev.map((c) => clsIds.includes(c.class_id) ? { ...c, parent_folder_id: f.folder_id } : c));
        await Promise.allSettled(clsIds.map((id) => api.put(`/workspaces/${current.workspace_id}/classes/${id}`, { parent_folder_id: f.folder_id })));
        setExpandedFolders((s) => { const n = new Set(s); n.add(f.folder_id); return n; });
        setRenameTarget({ kind: "folder", id: f.folder_id, value: f.name });
        setTimeout(() => { renameInputRef.current?.focus(); renameInputRef.current?.select(); }, 50);
        setSelectedClsIds(new Set());
        /* silent */
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [createFolder, update, loadWorkspaces, setWorkspaces, setClasses, current]);
  const [wsOpen, setWsOpen] = useState(false);
  const [wsEditing, setWsEditing] = useState(null); // workspace being edited
  // Sharing dialog state — opened via right-click → Share… (user request:
  // workspace sharing pub/private/secured).
  const [shareTarget, setShareTarget] = useState(null);
  const [shareMode, setShareMode] = useState("private");
  const [shareInviteesText, setShareInviteesText] = useState("");
  const [sharePassword, setSharePassword] = useState("");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [devLabOpen, setDevLabOpen] = useState(false);
  const [settingsTab, setSettingsTab] = useState("account");
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState(PRESET_COLORS[0]);
  const [newDesc, setNewDesc] = useState("");

  const [classDialogOpen, setClassDialogOpen] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [cName, setCName] = useState("");
  const [cColor, setCColor] = useState(PRESET_COLORS[0]);
  const [cDesc, setCDesc] = useState("");
  const [cScope, setCScope] = useState("local");

  // inline rename state
  const [renameTarget, setRenameTarget] = useState(null); // {kind, id, value}
  // Sidebar right-click context menu. Replaces stock browser menu for
  // workspaces / classes / folders. { x, y, kind: 'workspace'|'class'|'folder', item }
  const [sidebarCtx, setSidebarCtx] = useState(null);
  const [folderNukeTarget, setFolderNukeTarget] = useState(null);
  const [folderNukeConfirm, setFolderNukeConfirm] = useState(false);
  const [wsDeleteTarget, setWsDeleteTarget] = useState(null);
  const [wsDeleteTyped, setWsDeleteTyped] = useState("");
  // Hydrate the share dialog when a target is set so it shows the workspace's
  // current settings.
  useEffect(() => {
    if (!shareTarget) return;
    setShareMode(shareTarget.share_mode || "private");
    setShareInviteesText((shareTarget.share_invitees || []).join(", "));
    setSharePassword("");
  }, [shareTarget]);

  // Bridge events fired by the calendar's top-of-page header.
  useEffect(() => {
    const onOpenShare = (e) => setShareTarget(e.detail?.workspace || current);
    const onOpenNewWs = () => openNewWs();
    const onOpenNewFolder = (e) => addFolderAndRename(e.detail?.kind || "workspace");
    window.addEventListener("crescendo:open-share", onOpenShare);
    window.addEventListener("crescendo:open-new-workspace", onOpenNewWs);
    window.addEventListener("crescendo:open-new-folder", onOpenNewFolder);
    return () => {
      window.removeEventListener("crescendo:open-share", onOpenShare);
      window.removeEventListener("crescendo:open-new-workspace", onOpenNewWs);
      window.removeEventListener("crescendo:open-new-folder", onOpenNewFolder);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current]);

  const openSidebarCtx = (e, kind, item) => {
    e.preventDefault(); e.stopPropagation();
    setSidebarCtx({ x: e.clientX, y: e.clientY, kind, item });
  };
  useEffect(() => {
    if (!sidebarCtx) return;
    const close = () => setSidebarCtx(null);
    window.addEventListener("click", close);
    window.addEventListener("keydown", (e) => { if (e.key === "Escape") close(); });
    return () => { window.removeEventListener("click", close); };
  }, [sidebarCtx]);
  const FOLDER_SORTS = [
    { key: "position", label: "Manual" },
    { key: "name", label: "Name (A→Z)" },
    { key: "name-desc", label: "Name (Z→A)" },
    { key: "created", label: "Created (newest)" },
    { key: "color", label: "Color" },
  ];

  // Section-wide sort for top-level classes (Global + Local). Persisted in
  // localStorage. User request: "Global/Local classes and folders should
  // have sorting via right-click context menu". Right-clicking the
  // Classes section header surfaces this picker.
  const [classSort, setClassSort] = useState(() => {
    try { return localStorage.getItem("crescendo:classSort") || "position"; } catch { return "position"; }
  });
  useEffect(() => {
    try { localStorage.setItem("crescendo:classSort", classSort); } catch {}
  }, [classSort]);
  const applyClassSort = (items) => {
    if (!classSort || classSort === "position") return items;
    const cmp = (a, b) => {
      if (classSort === "name") return (a.name || "").localeCompare(b.name || "");
      if (classSort === "name-desc") return (b.name || "").localeCompare(a.name || "");
      if (classSort === "created") return String(b.created_at || "").localeCompare(String(a.created_at || ""));
      if (classSort === "color") return String(a.color || "").localeCompare(String(b.color || ""));
      return 0;
    };
    return [...items].sort(cmp);
  };

  const renameInputRef = useRef(null);

  const onCreateWs = async () => {
    if (!newName.trim()) return toast.error("Name required");
    if (wsEditing) {
      await api.put(`/workspaces/${wsEditing.workspace_id}`, { name: newName.trim(), color: newColor, description: newDesc });
      await loadWorkspaces?.();
      /* silent */
    } else {
      await createWorkspace(newName.trim(), newColor, newDesc);
      /* silent */
    }
    setNewName(""); setNewColor(PRESET_COLORS[0]); setNewDesc(""); setWsOpen(false); setWsEditing(null);
  };

  const openNewWs = () => {
    setWsEditing(null);
    setNewName(""); setNewColor(PRESET_COLORS[0]); setNewDesc("");
    setWsOpen(true);
  };

  const openEditWs = (ws) => {
    setWsEditing(ws);
    setNewName(ws.name); setNewColor(ws.color || PRESET_COLORS[0]); setNewDesc(ws.description || "");
    setWsOpen(true);
  };

  const openNewClass = () => {
    setEditingClass(null);
    setCName(""); setCColor(PRESET_COLORS[0]); setCDesc(""); setCScope("local");
    setClassDialogOpen(true);
  };
  const openEditClass = (cls) => {
    setEditingClass(cls);
    setCName(cls.name); setCColor(cls.color); setCDesc(cls.description || ""); setCScope(cls.scope || "local");
    setClassDialogOpen(true);
  };
  const saveClass = async () => {
    if (!cName.trim()) return toast.error("Name required");
    if (editingClass) {
      await update(editingClass.class_id, { name: cName.trim(), color: cColor, description: cDesc, scope: cScope });
      /* silent */
    } else {
      await create({ name: cName.trim(), color: cColor, description: cDesc, scope: cScope });
      /* silent */
    }
    setClassDialogOpen(false);
  };

  const commitRename = async () => {
    if (!renameTarget) return;
    // Read from the live input if available (uncontrolled input pattern)
    const inputVal = renameInputRef.current?.value;
    const val = (inputVal !== undefined ? inputVal : renameTarget.value)?.trim();
    if (!val) { setRenameTarget(null); return; }
    try {
      if (renameTarget.kind === "workspace") {
        // Optimistic local rename so the sidebar doesn't blink (issue #11).
        setWorkspaces?.(workspaces.map((w) => w.workspace_id === renameTarget.id ? { ...w, name: val } : w));
        api.put(`/workspaces/${renameTarget.id}`, { name: val }).catch(() => {});
      } else if (renameTarget.kind === "class") {
        update(renameTarget.id, { name: val });
      }
      /* silent */
    } catch (err) {
      toast.error("Rename Failed");
    } finally {
      setRenameTarget(null);
    }
  };
  const deleteClass = async () => {
    if (!editingClass) return;
    await removeCls(editingClass.class_id);
    /* silent */
    setClassDialogOpen(false);
  };

  const isDraggingWs = { current: false };
  const onDragStart = (e, type) => {
    e.dataTransfer.setData("application/x-crescendo-node-type", type);
    e.dataTransfer.effectAllowed = "copy";
    // Broadcast the type so the calendar's ghost preview can render the right
    // color/shape while the drag is in flight (dataTransfer.getData() is
    // blocked in dragover for security).
    window.dispatchEvent(new CustomEvent("crescendo:drag-type", { detail: { type } }));
  };
  const onDragEnd = () => {
    window.dispatchEvent(new CustomEvent("crescendo:drag-type", { detail: { type: null } }));
  };

  // Workspace row renderer (used in folder tree + root). `nested` indents.
  const renderWorkspaceRow = (w, nested) => {
    const isRenamingWs = renameTarget?.kind === "workspace" && renameTarget?.id === w.workspace_id;
    const multiSelected = selectedWsIds.has(w.workspace_id);
    return (
      <div key={w.workspace_id}
        draggable={!isRenamingWs}
        onDragStart={(e) => {
          e.dataTransfer.setData("application/x-crescendo-ws-id", w.workspace_id);
          const batch = selectedWsIds.has(w.workspace_id) ? [...selectedWsIds] : [...selectedWsIds, w.workspace_id];
          e.dataTransfer.setData("application/x-crescendo-ws-ids", batch.join(","));
          e.dataTransfer.effectAllowed = "move";
        }}
        onDragOver={(e) => {
          if (e.dataTransfer.types.includes("application/x-crescendo-ws-id")) {
            e.preventDefault(); e.dataTransfer.dropEffect = "move";
            const rect = e.currentTarget.getBoundingClientRect();
            const after = e.clientY - rect.top > rect.height / 2;
            e.currentTarget.classList.toggle("reorder-hint-below", after);
            e.currentTarget.classList.toggle("reorder-hint-above", !after);
          }
        }}
        onDragLeave={(e) => { e.currentTarget.classList.remove("reorder-hint-below"); e.currentTarget.classList.remove("reorder-hint-above"); }}
        onDrop={async (e) => {
          const after = e.currentTarget.classList.contains("reorder-hint-below");
          e.currentTarget.classList.remove("reorder-hint-below"); e.currentTarget.classList.remove("reorder-hint-above");
          const draggedId = e.dataTransfer.getData("application/x-crescendo-ws-id");
          if (!draggedId || draggedId === w.workspace_id) return;
          e.stopPropagation(); e.preventDefault();
          // Honour the batch payload — multi-selection drag must move every
          // selected workspace, not just the one whose row triggered drag
          // (user bug: "grouping leaves out elements").
          const targetFolder = w.parent_folder_id || null;
          const batchData = e.dataTransfer.getData("application/x-crescendo-ws-ids");
          const batchIds = batchData ? batchData.split(",").filter(Boolean) : [draggedId];
          const idsToMove = batchIds.filter((id) => {
            const x = workspaces.find((y) => y.workspace_id === id);
            return x && (x.parent_folder_id || null) !== targetFolder;
          });
          if (idsToMove.length > 0) {
            setWorkspaces?.(workspaces.map((x) => idsToMove.includes(x.workspace_id) ? { ...x, parent_folder_id: targetFolder } : x));
            await Promise.allSettled(idsToMove.map((id) => api.put(`/workspaces/${id}`, { parent_folder_id: targetFolder })));
          }
          await reorderWorkspaces(draggedId, w.workspace_id, after);
        }}
        className={`group flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer transition-colors ${nested ? "ml-5" : ""} ${multiSelected ? "bg-[#0055FF]/20 ring-1 ring-[#0055FF]" : ""} ${current?.workspace_id === w.workspace_id ? "bg-[#1a1a1a]" : "hover:bg-[#151515]"}`}
        onContextMenu={(e) => openSidebarCtx(e, "workspace", w)}
        onClick={(e) => {
          if (isRenamingWs) return;
          // Multi-select: Ctrl/Cmd or Shift toggle membership (issue #11).
          if (e.shiftKey || e.ctrlKey || e.metaKey) {
            e.preventDefault();
            setSelectedWsIds((prev) => { const n = new Set(prev); n.has(w.workspace_id) ? n.delete(w.workspace_id) : n.add(w.workspace_id); return n; });
            return;
          }
          setSelectedWsIds(new Set());
          setCurrent(w.workspace_id);
        }}
        onDoubleClick={(e) => { e.stopPropagation(); setRenameTarget({ kind: "workspace", id: w.workspace_id, value: w.name }); setTimeout(() => renameInputRef.current?.focus(), 10); }}
        data-testid={`workspace-option-${w.workspace_id}`}
      >
        <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: w.color }} />
        {isRenamingWs ? (
          <input ref={renameInputRef} value={renameTarget.value}
            onChange={(e) => setRenameTarget({ ...renameTarget, value: e.target.value })}
            onBlur={commitRename}
            onKeyDown={(e) => { if (e.key === "Enter") commitRename(); if (e.key === "Escape") setRenameTarget(null); }}
            className="flex-1 bg-[#0A0A0A] border border-[#2A2A2A] rounded px-1 text-sm outline-none focus:border-[#555]"
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span className="flex-1 text-sm truncate">{w.name}</span>
        )}
        {current?.workspace_id === w.workspace_id && !isRenamingWs && <Check className="w-3.5 h-3.5 text-[#A1A1AA]" />}
        <button onClick={(e) => { e.stopPropagation(); openEditWs(w); }} className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-[#2a2a2a]" data-testid={`edit-workspace-${w.workspace_id}`} aria-label="Edit workspace">
          <Edit3 className="w-3 h-3 text-[#A1A1AA] hover:text-[#EDEDED]" />
        </button>
        {workspaces.length > 1 && (
          <button onClick={async (e) => {
            e.stopPropagation();
            const ok = await confirmDelete({ title: "Delete Workspace?", message: `"${w.name}" and all its events will be permanently removed.`, confirmLabel: "Delete", checkboxLabel: "I understand this is permanent" });
            if (ok) deleteWorkspace(w.workspace_id);
          }} className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-[#2a2a2a]">
            <Trash2 className="w-3 h-3 text-[#FF3B30]" />
          </button>
        )}
      </div>
    );
  };

  // Class row renderer. `withinFolder` shows a globe icon for global classes (since they're out of the 'Global' header).
  const renderClassRow = (cls, nested, withinFolder = false) => {
    const active = activeClassId === cls.class_id;
    const isRenaming = renameTarget?.kind === "class" && renameTarget?.id === cls.class_id;
    const multiSelected = selectedClsIds.has(cls.class_id);
    return (
      <div key={cls.class_id}
        draggable={!isRenaming}
        onDragStart={(e) => {
          e.dataTransfer.setData("application/x-crescendo-class-id", cls.class_id);
          // Pack the full selection into a separate payload so drop targets
          // don't rely on possibly-stale React state when grouping.
          const batch = selectedClsIds.has(cls.class_id) ? [...selectedClsIds] : [...selectedClsIds, cls.class_id];
          e.dataTransfer.setData("application/x-crescendo-class-ids", batch.join(","));
          e.dataTransfer.effectAllowed = "move";
        }}
        onDragOver={(e) => {
          if (e.dataTransfer.types.includes("application/x-crescendo-class-id")) {
            e.preventDefault(); e.dataTransfer.dropEffect = "move";
            const rect = e.currentTarget.getBoundingClientRect();
            const after = e.clientY - rect.top > rect.height / 2;
            e.currentTarget.classList.toggle("reorder-hint-below", after);
            e.currentTarget.classList.toggle("reorder-hint-above", !after);
          }
        }}
        onDragLeave={(e) => { e.currentTarget.classList.remove("reorder-hint-below"); e.currentTarget.classList.remove("reorder-hint-above"); }}
        onDrop={async (e) => {
          const after = e.currentTarget.classList.contains("reorder-hint-below");
          e.currentTarget.classList.remove("reorder-hint-below"); e.currentTarget.classList.remove("reorder-hint-above");
          const draggedId = e.dataTransfer.getData("application/x-crescendo-class-id");
          if (!draggedId || draggedId === cls.class_id) return;
          e.stopPropagation(); e.preventDefault();
          const targetFolder = cls.parent_folder_id || null;
          // Honour the batch — dragging a multi-selection across folder
          // boundaries was leaving siblings behind (user bug). Move every
          // batch id whose current parent differs from the target.
          const batchData = e.dataTransfer.getData("application/x-crescendo-class-ids");
          const batchIds = batchData ? batchData.split(",").filter(Boolean) : [draggedId];
          const idsToMove = batchIds.filter((id) => {
            const c = classes.find((x) => x.class_id === id);
            return c && (c.parent_folder_id || null) !== targetFolder;
          });
          if (idsToMove.length > 0) {
            setClasses?.(classes.map((x) => idsToMove.includes(x.class_id) ? { ...x, parent_folder_id: targetFolder } : x));
            await Promise.allSettled(idsToMove.map((id) => update(id, { parent_folder_id: targetFolder })));
          }
          await reorderClasses(draggedId, cls.class_id, after);
        }}
        className={`group flex items-center gap-2 px-2 py-1 rounded cursor-pointer text-sm ${nested ? "ml-5" : ""} ${multiSelected ? "bg-[#0055FF]/20 ring-1 ring-[#0055FF]" : ""} ${active ? "bg-[#1a1a1a]" : "hover:bg-[#151515]"}`}
        onContextMenu={(e) => openSidebarCtx(e, "class", cls)}
        onClick={(e) => {
          if (isRenaming) return;
          if (e.shiftKey || e.ctrlKey || e.metaKey) {
            e.preventDefault();
            setSelectedClsIds((prev) => { const n = new Set(prev); n.has(cls.class_id) ? n.delete(cls.class_id) : n.add(cls.class_id); return n; });
            return;
          }
          setSelectedClsIds(new Set());
          setActiveClassId(active ? null : cls.class_id);
        }}
        onDoubleClick={(e) => { e.stopPropagation(); setRenameTarget({ kind: "class", id: cls.class_id, value: cls.name }); setTimeout(() => renameInputRef.current?.focus(), 10); }}
        data-testid={`class-item-${cls.class_id}`}
      >
        <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: cls.color }} />
        {isRenaming ? (
          <input
            key={`rename-class-${cls.class_id}`}
            autoFocus
            defaultValue={renameTarget.value}
            ref={(el) => {
              // Store ref for commitRename to read value
              if (el) el._classId = cls.class_id;
              renameInputRef.current = el;
            }}
            onBlur={() => {
              if (renameInputRef.current) {
                setRenameTarget(r => r ? { ...r, value: renameInputRef.current.value } : r);
              }
              setTimeout(commitRename, 0);
            }}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === "Enter") {
                setRenameTarget(r => r ? { ...r, value: e.target.value } : r);
                setTimeout(commitRename, 0);
              }
              if (e.key === "Escape") setRenameTarget(null);
            }}
            className="flex-1 bg-[#0A0A0A] border border-[#2A2A2A] rounded px-1 text-xs outline-none focus:border-[#555]"
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          // Keep the name + optional globe tightly adjacent (issue #8), not
          // pushed to opposite ends by a flex-1 spacer.
          <div className="flex-1 flex items-center gap-1.5 min-w-0">
            <span className="truncate">{cls.name}</span>
            {withinFolder && cls.scope === "global" && (
              <Globe className="w-3 h-3 text-[#A1A1AA] flex-shrink-0" title="Global Class" data-testid={`global-badge-${cls.class_id}`} />
            )}
            {classTotals[cls.class_id] ? (
              <span className="ml-auto text-[9px] text-[#A1A1AA] font-mono flex-shrink-0 tabular-nums" data-testid={`class-total-${cls.class_id}`}>
                {fmtMinutes(classTotals[cls.class_id])}
              </span>
            ) : null}
          </div>
        )}
        <button onClick={(e) => { e.stopPropagation(); openEditClass(cls); }}
          className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-[#2a2a2a]">
          <Edit3 className="w-3 h-3 text-[#A1A1AA] hover:text-[#EDEDED]" />
        </button>
      </div>
    );
  };

  // Folder row (for classes section) — similar structure; returns JSX for one folder + children
  // Apply folder.sort_by to a list of items. Falls back to insertion order.
  const sortByFolder = (items, folder, kind) => {
    if (!folder?.sort_by || folder.sort_by === "position") return items;
    const key = folder.sort_by;
    const cmp = (a, b) => {
      if (key === "name") return (a.name || "").localeCompare(b.name || "");
      if (key === "name-desc") return (b.name || "").localeCompare(a.name || "");
      if (key === "created") return String(b.created_at || "").localeCompare(String(a.created_at || ""));
      if (key === "color") return String(a.color || "").localeCompare(String(b.color || ""));
      return 0;
    };
    return [...items].sort(cmp);
  };

  const renderClassFolder = (f, childrenList) => {
    const expanded = expandedFolders.has(f.folder_id);
    const sortedChildren = sortByFolder(childrenList, f, "class");
    const isRenamingF = renameTarget?.kind === "folder" && renameTarget?.id === f.folder_id;
    return (
      <div key={f.folder_id} data-testid={`class-folder-${f.folder_id}`}
        onDragOver={(e) => {
          // Don't stopPropagation: the parent class-list also wants to
          // mark itself as a drop target so the user can drop on the empty
          // area outside this folder to ungroup (user request: drag-out
          // should "just work" without a separate ungroup zone).
          if (e.dataTransfer.types.includes("application/x-crescendo-class-id")) {
            e.preventDefault();
            e.dataTransfer.dropEffect = "move";
            e.currentTarget.classList.add("drop-target");
          }
        }}
        onDragLeave={(e) => e.currentTarget.classList.remove("drop-target")}
        onDrop={async (e) => {
          e.currentTarget.classList.remove("drop-target");
          const id = e.dataTransfer.getData("application/x-crescendo-class-id");
          if (!id) return;
          // No-op when ALL dragged items already belong to this folder —
          // lets the parent class-list's onDrop run if the user is trying
          // to ungroup. Otherwise this would silently re-pin them to the
          // same folder and the ungroup operation would never reach the
          // parent (recurring user bug).
          const batch = e.dataTransfer.getData("application/x-crescendo-class-ids");
          const ids = batch ? batch.split(",").filter(Boolean) : [id];
          const allInThisFolder = ids.every((cid) => classes.find((c) => c.class_id === cid)?.parent_folder_id === f.folder_id);
          if (allInThisFolder) return; // bubble to parent
          e.stopPropagation();
          setClasses?.(classes.map((c) => ids.includes(c.class_id) ? { ...c, parent_folder_id: f.folder_id } : c));
          for (const cid of ids) {
            try { await update(cid, { parent_folder_id: f.folder_id }); } catch { /* continue */ }
          }
          setSelectedClsIds(new Set());
          /* silent */
        }}
      >
        <div className="group flex items-center gap-1 px-1 py-1 rounded hover:bg-[#151515] cursor-pointer"
          onClick={() => !isRenamingF && toggleFolder(f.folder_id)}
          onContextMenu={(e) => openSidebarCtx(e, "folder", f)}
          onDoubleClick={(e) => { e.stopPropagation(); setRenameTarget({ kind: "folder", id: f.folder_id, value: f.name }); setTimeout(() => renameInputRef.current?.focus(), 10); }}
        >
          {expanded ? <ChevronDown className="w-3 h-3 text-[#A1A1AA]" /> : <ChevronRight className="w-3 h-3 text-[#A1A1AA]" />}
          <FolderIcon className="w-3.5 h-3.5" style={{ color: f.color || "#A1A1AA" }} />
          {isRenamingF ? (
            <input ref={renameInputRef} value={renameTarget.value}
              onChange={(e) => setRenameTarget({ ...renameTarget, value: e.target.value })}
              onBlur={async () => { if (renameTarget?.value?.trim()) { await updateFolder(f.folder_id, { name: renameTarget.value.trim() }); } setRenameTarget(null); }}
              onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") setRenameTarget(null); }}
              className="flex-1 bg-[#0A0A0A] border border-[#2A2A2A] rounded px-1 text-xs outline-none focus:border-[#555]"
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <span className="flex-1 text-xs truncate text-[#EDEDED]">{f.name}</span>
          )}
          <button onClick={async (e) => { e.stopPropagation(); if (childrenList.length > 0) return toast.error("Folder not empty"); await removeFolder(f.folder_id); /* silent */ }}
            className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-[#2a2a2a]">
            <Trash2 className="w-3 h-3 text-[#FF3B30]" />
          </button>
        </div>
        {expanded && sortedChildren.map((c) => renderClassRow(c, true, true))}
      </div>
    );
  };

  const { sidebarStyle } = useBg();

  return (
    <aside className="h-full flex flex-col" data-testid="sidebar" data-cresc-sidebar
      style={{ ...sidebarStyle, background: "var(--cresc-surface)", borderRight: "1px solid var(--cresc-border-sub)" }}
      onContextMenu={(e) => e.preventDefault()}
      onMouseLeave={() => { if (onAutoCollapseLeave) setTimeout(onAutoCollapseLeave, 250); }}
    >
      {/* ── Profile header ─────────────────────────────────────────────── */}
      <div className="flex items-center gap-3 px-4 pt-5 pb-3 flex-shrink-0">
        {/* Avatar */}
        <div className="w-9 h-9 rounded-xl flex items-center justify-center text-[13px] font-bold text-white flex-shrink-0 select-none"
          style={{ background: "var(--cresc-accent)" }}>
          {user?.name ? user.name[0].toUpperCase() : "C"}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-semibold truncate" style={{ color: "var(--cresc-text)" }}>
            {user?.name || "Crescendo"}
          </p>
          <p className="text-[10px] truncate" style={{ color: "var(--cresc-text-3)" }}>
            {current?.name || "My Workspace"}
          </p>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button onClick={openNewWs}
            className="w-6 h-6 rounded-lg flex items-center justify-center transition-colors"
            style={{ color: "var(--cresc-text-3)" }}
            onMouseEnter={e => e.currentTarget.style.background = "var(--cresc-surface-3)"}
            onMouseLeave={e => e.currentTarget.style.background = "transparent"}
            title="New workspace">
            <Plus className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => { setSettingsTab("account"); setSettingsOpen(true); }}
            className="w-6 h-6 rounded-lg flex items-center justify-center transition-colors"
            style={{ color: "var(--cresc-text-3)" }}
            onMouseEnter={e => e.currentTarget.style.background = "var(--cresc-surface-3)"}
            onMouseLeave={e => e.currentTarget.style.background = "transparent"}
            title="Settings">
            <SettingsIcon className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* ── Search ─────────────────────────────────────────────────────── */}
      <div className="px-4 pb-3 flex-shrink-0">
        <div className="flex items-center gap-2 px-3 py-2 rounded-xl"
          style={{ background: "var(--cresc-surface-2)", border: "1px solid var(--cresc-border-sub)" }}>
          <svg width="13" height="13" viewBox="0 0 16 16" fill="none" style={{ color: "var(--cresc-text-3)", flexShrink: 0 }}>
            <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M11 11l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          <SidebarSearch inline />
        </div>
      </div>
      {/* New/edit workspace dialog */}
      <Dialog open={wsOpen} onOpenChange={(v) => { setWsOpen(v); if (!v) setWsEditing(null); }}>
        <DialogContent className="bg-[#141414] border-[#2A2A2A] text-[#EDEDED]">
          <DialogHeader>
            <DialogTitle className="font-display">{wsEditing ? "Edit Workspace" : "New Workspace"}</DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">{wsEditing ? "Update the workspace name, color, or description." : "Pick a name and accent color."}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="meta-label block mb-2">NAME</label>
              <input value={newName} onChange={(e) => setNewName(e.target.value)}
                className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-md px-3 py-2 outline-none focus:border-[#555]"
                data-testid="new-workspace-name" />
            </div>
            <div>
              <label className="meta-label block mb-2">COLOR</label>
              <div className="flex gap-2 items-center flex-wrap">
                {PRESET_COLORS.map((c) => (
                  <button key={c} onClick={() => setNewColor(c)}
                    className={`w-7 h-7 rounded-full border-2 ${newColor === c ? "border-[#EDEDED]" : "border-transparent"}`}
                    style={{ background: c }} />
                ))}
                <label className="relative w-7 h-7 rounded-full cursor-pointer overflow-hidden border-2 border-dashed border-[#3A3A3A] flex items-center justify-center">
                  <input type="color" value={newColor} onChange={(e) => setNewColor(e.target.value)}
                    className="absolute inset-0 opacity-0 cursor-pointer" data-testid="custom-color-input" />
                  <div className="w-4 h-4 rounded-full" style={{ background: newColor }} />
                </label>
                <span className="text-xs text-[#A1A1AA]">{newColor}</span>
              </div>
            </div>
            <div>
              <label className="meta-label block mb-2">DESCRIPTION</label>
              <textarea value={newDesc} onChange={(e) => setNewDesc(e.target.value)} rows={2}
                placeholder="What's this workspace for?"
                className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-md px-3 py-2 outline-none focus:border-[#555] text-sm" />
            </div>
          </div>
          <DialogFooter>
            <button onClick={onCreateWs} className="bg-[#EDEDED] text-[#0A0A0A] px-4 py-2 rounded-md font-medium hover:bg-white" data-testid="create-workspace-submit">{wsEditing ? "Save" : "Create"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sharing dialog — public / invite / secured (user request). */}
      <Dialog
        open={!!shareTarget}
        onOpenChange={(v) => { if (!v) setShareTarget(null); }}
      >
        <DialogContent className="bg-[#141414] border-[#2A2A2A] text-[#EDEDED] max-w-md" data-testid="share-workspace-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Share workspace</DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">Pick how this workspace can be accessed.</DialogDescription>
          </DialogHeader>
          {shareTarget && (
            <ShareDialogBody
              workspace={shareTarget}
              mode={shareMode} setMode={setShareMode}
              invitees={shareInviteesText} setInvitees={setShareInviteesText}
              password={sharePassword} setPassword={setSharePassword}
              onClose={() => setShareTarget(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* New Template dialog (user request: replace inline HTML prompt with
          a proper dialog so users can name the template). The actual save
          fires the existing `crescendo:save-template-from-selection` event
          consumed by CalendarView (which carries the user's current event
          selection). */}
      <Dialog open={showNewTplDialog} onOpenChange={(v) => { if (!v) { setShowNewTplDialog(false); setNewTplName(""); } }}>
        <DialogContent className="bg-[#141414] border-[#2A2A2A] text-[#EDEDED] max-w-sm" data-testid="new-template-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">New template</DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              Pick a name. The template snapshots whichever events you currently have selected on the calendar.
            </DialogDescription>
          </DialogHeader>
          <input
            autoFocus
            value={newTplName}
            onChange={(e) => setNewTplName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { document.getElementById("tpl-create-confirm")?.click(); } }}
            placeholder="Template name"
            className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-md px-3 py-2 outline-none focus:border-[#5A90F5]"
            data-testid="new-template-name-input"
          />
          <DialogFooter>
            <button onClick={() => { setShowNewTplDialog(false); setNewTplName(""); }} className="px-3 py-1.5 rounded text-xs border border-[#2A2A2A] hover:bg-[#1a1a1a]" data-testid="new-template-cancel">Cancel</button>
            <button
              id="tpl-create-confirm"
              onClick={() => {
                if (!newTplName.trim()) return;
                window.dispatchEvent(new CustomEvent("crescendo:save-template-from-selection", { detail: { name: newTplName.trim() } }));
                setShowNewTplDialog(false);
                setNewTplName("");
              }}
              className="px-3 py-1.5 rounded text-xs bg-[#5A90F5] text-[#0A0A0A] font-semibold hover:bg-[#7AA5F8]"
              data-testid="new-template-create"
            >
              Create
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Persistent search bar moved inline with the logo at the top dock. */}


      {/* Project Tree — replaces global tab navigation */}
      <ProjectTree
        onSelectElement={(element) => {
          const envTypes = ["calendar","resources","automations"];
          if (envTypes.includes(element.type)) {
            setMainTab(element.type);
          }
        }}
        onSelectProject={(project) => {
          // Navigate to overview tab to show project dashboard
          setMainTab("overview");
          if (typeof window !== "undefined") {
            window.dispatchEvent(new CustomEvent("crescendo:select-project", { detail: project }));
          }
        }}
        onSelectOverview={() => {
          setMainTab("overview");
          window.dispatchEvent(new CustomEvent("crescendo:select-overview"));
        }}
        selectedId={null}
        selectedProjectId={null}
        isOverviewActive={mainTab === "overview"}
      />

      

      <div className="flex-1 overflow-y-auto">
      {mainTab === "activity" ? (
        <ActivityErrorBoundary><ActivityFeed /></ActivityErrorBoundary>
      ) : mainTab === "resources" ? (
        /* Documents sidebar swap — dedicated doc list + new/upload controls so
           the editor gets maximum width on the right (user request). */
        <div className="px-3 py-3" data-testid="documents-sidebar">
          <div className="flex items-center justify-between mb-2 px-1">
            <span className="meta-label">Documents</span>
            <div className="flex items-center gap-1">
              <label
                className="p-1 rounded hover:bg-[#1a1a1a] cursor-pointer"
                title="Upload"
                data-testid="docs-upload-btn"
              >
                <Upload className="w-4 h-4 text-[#A1A1AA]" />
                <input
                  type="file"
                  accept=".txt,.md,.json,.csv"
                  onChange={async (e) => {
                    const f = e.target.files?.[0]; if (!f) return;
                    const text = await f.text().catch(() => "");
                    const doc = await createDoc({ name: f.name, kind: "text", body: text });
                    setActiveDocId(doc.doc_id);
                    /* silent: uploaded */
                    e.target.value = "";
                  }}
                  className="hidden"
                />
              </label>
              <button
                onClick={() => addFolderAndRename("document")}
                className="p-1 rounded hover:bg-[#1a1a1a]"
                title="New folder"
                data-testid="docs-new-folder-btn"
              >
                <FolderPlus className="w-4 h-4 text-[#A1A1AA]" />
              </button>
              <button
                onClick={async () => {
                  const doc = await createDoc({ name: "Untitled", kind: "markdown", body: "" });
                  setActiveDocId(doc.doc_id);
                }}
                className="p-1 rounded hover:bg-[#1a1a1a]"
                title="New document"
                data-testid="docs-new-btn"
              >
                <Plus className="w-4 h-4 text-[#A1A1AA]" />
              </button>
            </div>
          </div>
          {allDocs.length === 0 && folders.filter((f) => f.kind === "document").length === 0 && (
            <div className="px-3 py-6 text-[11px] text-[#A1A1AA] text-center">
              No documents yet. Hit + to create one.
            </div>
          )}
          {(() => {
            const renderDocItem = (d, depth = 0) => {
              const isRenamingDoc = renameTarget?.kind === "doc" && renameTarget?.id === d.doc_id;
              return (
                <div
                  key={d.doc_id}
                  draggable
                  onDragStart={(e) => {
                    e.stopPropagation();
                    e.dataTransfer.setData("application/x-crescendo-doc-id", d.doc_id);
                    e.dataTransfer.effectAllowed = "move";
                  }}
                  onClick={() => !isRenamingDoc && setActiveDocId(d.doc_id)}
                  onContextMenu={(e) => openSidebarCtx(e, "doc", d)}
                  onDoubleClick={(e) => {
                    e.stopPropagation();
                    setRenameTarget({ kind: "doc", id: d.doc_id, value: d.name });
                    setTimeout(() => { renameInputRef.current?.focus(); renameInputRef.current?.select(); }, 10);
                  }}
                  className={`group px-2 py-1.5 rounded cursor-pointer border border-transparent ${activeDocId === d.doc_id ? "bg-[#1a1a1a] border-[#2A2A2A]" : "hover:bg-[#151515]"}`}
                  data-testid={`docs-list-item-${d.doc_id}`}
                  style={{ marginLeft: depth * 8 }}
                >
                  <div className="flex items-center gap-2">
                    {d.kind === "master" ? (
                      <>
                        <FileText className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "#5A90F5" }} />
                        <span className="text-[8px] font-bold bg-[#5A90F5]/20 text-[#5A90F5] px-1 py-0.5 rounded flex-shrink-0 leading-none uppercase tracking-wider">master</span>
                      </>
                    ) : d.kind === "link" ? (
                      <LinkIcon className="w-3.5 h-3.5 text-[#A1A1AA] flex-shrink-0" />
                    ) : (
                      <FileText className="w-3.5 h-3.5 text-[#A1A1AA] flex-shrink-0" />
                    )}
                    {/* Class color indicator — small dot if the doc has
                        a class assigned. Iter33 user request: "show the
                        class color somewhere on the document in the
                        sidebar". */}
                    {(() => {
                      if (!d.class_id) return null;
                      const cls = classes.find((c) => c.class_id === d.class_id);
                      if (!cls) return null;
                      return (
                        <span
                          className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                          style={{ background: cls.color }}
                          title={`Class: ${cls.name}`}
                          data-testid={`docs-list-item-class-dot-${d.doc_id}`}
                        />
                      );
                    })()}
                    {isRenamingDoc ? (
                      <input
                        ref={renameInputRef}
                        value={renameTarget.value}
                        onChange={(e) => setRenameTarget({ ...renameTarget, value: e.target.value })}
                        onClick={(e) => e.stopPropagation()}
                        onBlur={async () => {
                          const v = renameTarget?.value?.trim();
                          if (v && v !== d.name) {
                            try {
                              await updateDoc(d.doc_id, {
                                name: v, kind: d.kind, body: d.body || "",
                                mime_type: d.mime_type || null,
                                workspace_id: d.workspace_id || null,
                                parent_folder_id: d.parent_folder_id || null,
                              });
                            } catch {}
                          }
                          setRenameTarget(null);
                        }}
                        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") setRenameTarget(null); }}
                        className="flex-1 bg-[#0A0A0A] border border-[#2A2A2A] rounded px-1 text-xs outline-none focus:border-[#555]"
                        data-testid={`docs-rename-input-${d.doc_id}`}
                      />
                    ) : (
                      <span className={`text-xs truncate flex-1 ${d.kind === "master" ? "text-[#5A90F5] font-medium" : ""}`}>{d.name}</span>
                    )}
                    {/* + button on each doc — opens popover with add menu
                        (user request: + on docs/tabs should show the
                        add-menu, not auto-create a tab). */}
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          onClick={(e) => e.stopPropagation()}
                          className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-[#2a2a2a] text-[#A1A1AA]"
                          title="Add"
                          data-testid={`docs-add-tab-${d.doc_id}`}
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent align="end" className="bg-[#141414] border-[#2A2A2A] text-[#EDEDED] p-1 w-44">
                        <button
                          onClick={async () => {
                            try {
                              const tabs = (d.tabs || []).concat([{ tab_id: `tab_${Date.now().toString(36)}`, name: "New Tab", body: "" }]);
                              await updateDoc(d.doc_id, {
                                name: d.name, kind: d.kind, body: d.body || "",
                                mime_type: d.mime_type, workspace_id: d.workspace_id,
                                parent_folder_id: d.parent_folder_id, tabs,
                              });
                              reloadDocs?.();
                            } catch {}
                          }}
                          className="w-full text-left px-2 py-1.5 rounded text-sm hover:bg-[#1a1a1a] flex items-center gap-2"
                          data-testid={`docs-add-newtab-${d.doc_id}`}
                        >
                          <FileEdit className="w-3.5 h-3.5" /> New tab
                        </button>
                        <button
                          onClick={async () => { try { await createDoc({ name: "Untitled", kind: "markdown", body: "", workspace_id: current?.workspace_id, parent_folder_id: d.parent_folder_id || null }); } catch {} }}
                          className="w-full text-left px-2 py-1.5 rounded text-sm hover:bg-[#1a1a1a] flex items-center gap-2"
                          data-testid={`docs-add-newdoc-${d.doc_id}`}
                        >
                          <FileText className="w-3.5 h-3.5" /> New document
                        </button>
                        <button
                          onClick={() => addFolderAndRename("document", d.parent_folder_id || null)}
                          className="w-full text-left px-2 py-1.5 rounded text-sm hover:bg-[#1a1a1a] flex items-center gap-2"
                          data-testid={`docs-add-newfolder-${d.doc_id}`}
                        >
                          <FolderPlus className="w-3.5 h-3.5" /> New folder
                        </button>
                      </PopoverContent>
                    </Popover>
                    {/* Download icon removed — moved into right-click menu
                        per latest user request. */}
                  </div>
                  {/* Tabs nested under the doc as sub-items (user request:
                      "when a new tab is added, it should show in the
                      sidebar as a sub-item of the document"). */}
                  {Array.isArray(d.tabs) && d.tabs.length > 0 && (
                    <div className="ml-5 mt-0.5 border-l border-[#2A2A2A] pl-2 space-y-0.5" data-testid={`docs-tabs-${d.doc_id}`}>
                      {d.tabs.map((t) => {
                        const isRenamingTab = renameTarget?.kind === "tab" && renameTarget?.id === t.tab_id;
                        return (
                          <div
                            key={t.tab_id}
                            onClick={(e) => {
                              if (isRenamingTab) return;
                              e.stopPropagation();
                              setActiveDocId(d.doc_id);
                              window.dispatchEvent(new CustomEvent("crescendo:docs-open-tab", { detail: { doc_id: d.doc_id, tab_id: t.tab_id } }));
                            }}
                            onDoubleClick={(e) => {
                              // Double-click → rename (user request).
                              e.stopPropagation(); e.preventDefault();
                              setRenameTarget({ kind: "tab", id: t.tab_id, doc_id: d.doc_id, value: t.name });
                            }}
                            onContextMenu={(e) => {
                              e.preventDefault(); e.stopPropagation();
                              setSidebarCtx({ x: e.clientX, y: e.clientY, kind: "doc-tab", item: { ...t, doc_id: d.doc_id } });
                            }}
                            className="group/tab flex items-center gap-1.5 px-1.5 py-1 rounded text-[11px] text-[#A1A1AA] hover:bg-[#151515] cursor-pointer"
                            data-testid={`docs-tab-row-${t.tab_id}`}
                          >
                            <span className="text-[10px] opacity-60">▸</span>
                            {isRenamingTab ? (
                              <input
                                autoFocus
                                value={renameTarget.value}
                                onChange={(ev) => setRenameTarget({ ...renameTarget, value: ev.target.value })}
                                onClick={(ev) => ev.stopPropagation()}
                                onBlur={async () => {
                                  const v = renameTarget?.value?.trim();
                                  setRenameTarget(null);
                                  if (!v || v === t.name) return;
                                  try {
                                    const tabs = (d.tabs || []).map((x) => x.tab_id === t.tab_id ? { ...x, name: v } : x);
                                    await updateDoc(d.doc_id, {
                                      name: d.name, kind: d.kind, body: d.body || "",
                                      mime_type: d.mime_type, workspace_id: d.workspace_id,
                                      parent_folder_id: d.parent_folder_id, tabs,
                                    });
                                    reloadDocs?.();
                                  } catch {}
                                }}
                                onKeyDown={(ev) => { if (ev.key === "Enter") ev.currentTarget.blur(); if (ev.key === "Escape") setRenameTarget(null); }}
                                className="flex-1 bg-[#0A0A0A] border border-[#2A2A2A] rounded px-1 text-[11px] outline-none focus:border-[#5A90F5]"
                                data-testid={`docs-tab-rename-input-${t.tab_id}`}
                              />
                            ) : (
                              <span className="flex-1 truncate">{t.name}</span>
                            )}
                            {/* Hover + button on tab → opens add-menu popover. */}
                            <Popover>
                              <PopoverTrigger asChild>
                                <button
                                  onClick={(ev) => ev.stopPropagation()}
                                  className="opacity-0 group-hover/tab:opacity-100 p-0.5 rounded hover:bg-[#2a2a2a] text-[#A1A1AA]"
                                  title="Add"
                                  data-testid={`docs-tab-plus-${t.tab_id}`}
                                >
                                  <Plus className="w-3 h-3" />
                                </button>
                              </PopoverTrigger>
                              <PopoverContent align="end" className="bg-[#141414] border-[#2A2A2A] text-[#EDEDED] p-1 w-44">
                                <button
                                  onClick={async () => {
                                    try {
                                      const tabs = (d.tabs || []).concat([{ tab_id: `tab_${Date.now().toString(36)}`, name: "New Tab", body: "" }]);
                                      await updateDoc(d.doc_id, {
                                        name: d.name, kind: d.kind, body: d.body || "",
                                        mime_type: d.mime_type, workspace_id: d.workspace_id,
                                        parent_folder_id: d.parent_folder_id, tabs,
                                      });
                                      reloadDocs?.();
                                    } catch { toast.error("Couldn't add tab"); }
                                  }}
                                  className="w-full text-left px-2 py-1.5 rounded text-sm hover:bg-[#1a1a1a] flex items-center gap-2"
                                  data-testid={`docs-tab-plus-newtab-${t.tab_id}`}
                                >
                                  <FileEdit className="w-3.5 h-3.5" /> New tab
                                </button>
                                <button
                                  onClick={async () => {
                                    try {
                                      const ws = current?.workspace_id;
                                      if (!ws) { toast.error("No active workspace"); return; }
                                      await createDoc({ name: "Untitled", kind: "markdown", body: "", workspace_id: ws, parent_folder_id: d.parent_folder_id || null });
                                      reloadDocs?.();
                                    } catch { toast.error("Couldn't create document"); }
                                  }}
                                  className="w-full text-left px-2 py-1.5 rounded text-sm hover:bg-[#1a1a1a] flex items-center gap-2"
                                  data-testid={`docs-tab-plus-newdoc-${t.tab_id}`}
                                >
                                  <FileText className="w-3.5 h-3.5" /> New document
                                </button>
                                <button
                                  onClick={() => { try { addFolderAndRename("document", d.parent_folder_id || null); } catch { toast.error("Couldn't create folder"); } }}
                                  className="w-full text-left px-2 py-1.5 rounded text-sm hover:bg-[#1a1a1a] flex items-center gap-2"
                                  data-testid={`docs-tab-plus-newfolder-${t.tab_id}`}
                                >
                                  <FolderPlus className="w-3.5 h-3.5" /> New folder
                                </button>
                              </PopoverContent>
                            </Popover>
                            {/* Trash icon removed — delete is now in the
                                right-click context menu only (user request). */}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            };
            const renderDocFolder = (parentFolderId, depth) => {
              const childFolders = folders.filter((f) => f.kind === "document" && (f.parent_folder_id || null) === parentFolderId);
              const docsHere = allDocs
                .filter((d) => (d.parent_folder_id || null) === parentFolderId)
                // Master docs always pin to top of their folder
                .sort((a, b) => (a.kind === "master" ? -1 : b.kind === "master" ? 1 : 0));
              const out = [];
              childFolders.forEach((f) => {
                const expanded = expandedFolders.has(f.folder_id);
                const isRenamingF = renameTarget?.kind === "folder" && renameTarget?.id === f.folder_id;
                out.push(
                  <div key={f.folder_id} data-testid={`doc-folder-${f.folder_id}`}
                    onDragOver={(e) => { if (e.dataTransfer.types.includes("application/x-crescendo-doc-id") || e.dataTransfer.types.includes("application/x-crescendo-doc-folder-id")) { e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = "move"; e.currentTarget.classList.add("drop-target"); } }}
                    onDragLeave={(e) => e.currentTarget.classList.remove("drop-target")}
                    onDrop={async (e) => {
                      e.currentTarget.classList.remove("drop-target");
                      e.stopPropagation();
                      const did = e.dataTransfer.getData("application/x-crescendo-doc-id");
                      if (did) {
                        const d = allDocs.find((x) => x.doc_id === did);
                        if (d) {
                          try { await updateDoc(did, { name: d.name, kind: d.kind, body: d.body || "", mime_type: d.mime_type, workspace_id: d.workspace_id, parent_folder_id: f.folder_id }); reloadDocs?.(); } catch {}
                        }
                        return;
                      }
                      const fid = e.dataTransfer.getData("application/x-crescendo-doc-folder-id");
                      if (fid && fid !== f.folder_id && depth < 2) {
                        try { await updateFolder(fid, { parent_folder_id: f.folder_id }); } catch {}
                      }
                    }}
                    style={{ marginLeft: depth * 8 }}
                  >
                    <div className="group flex items-center gap-1 px-1 py-1 rounded hover:bg-[#151515] cursor-pointer"
                      draggable
                      onDragStart={(e) => {
                        e.stopPropagation();
                        e.dataTransfer.setData("application/x-crescendo-doc-folder-id", f.folder_id);
                        e.dataTransfer.effectAllowed = "move";
                      }}
                      onClick={() => !isRenamingF && toggleFolder(f.folder_id)}
                      onDoubleClick={(e) => { e.stopPropagation(); setRenameTarget({ kind: "folder", id: f.folder_id, value: f.name }); setTimeout(() => renameInputRef.current?.focus(), 10); }}
                      onContextMenu={(e) => openSidebarCtx(e, "doc-folder", f)}
                    >
                      {expanded ? <ChevronDown className="w-3 h-3 text-[#A1A1AA]" /> : <ChevronRight className="w-3 h-3 text-[#A1A1AA]" />}
                      <FolderIcon className="w-3.5 h-3.5" style={{ color: f.color || "#A1A1AA" }} />
                      {isRenamingF ? (
                        <input ref={renameInputRef} value={renameTarget.value}
                          onChange={(e) => setRenameTarget({ ...renameTarget, value: e.target.value })}
                          onBlur={async () => { if (renameTarget?.value?.trim()) { await updateFolder(f.folder_id, { name: renameTarget.value.trim() }); } setRenameTarget(null); }}
                          onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") setRenameTarget(null); }}
                          className="flex-1 bg-[#0A0A0A] border border-[#2A2A2A] rounded px-1 text-xs outline-none focus:border-[#555]"
                          onClick={(e) => e.stopPropagation()}
                        />
                      ) : (
                        <span className="flex-1 text-xs truncate text-[#EDEDED]">{f.name}</span>
                      )}
                      {/* Master document button — only shown when folder has no master doc yet */}
                      {(() => {
                        const hasMaster = allDocs.some(d => d.parent_folder_id === f.folder_id && d.kind === "master" && !d.deleted_at);
                        if (hasMaster) return (
                          <span className="opacity-0 group-hover:opacity-100 px-1 text-[9px] text-[#5A90F5] font-medium flex-shrink-0" title="This folder has a master document">M</span>
                        );
                        return (
                          <button onClick={async (e) => { e.stopPropagation();
                            try {
                              const ws = current?.workspace_id;
                              if (!ws) { toast.error("No active workspace"); return; }
                              await createDoc({ name: "Master Document", kind: "master", body: "", workspace_id: ws, parent_folder_id: f.folder_id });
                              reloadDocs?.();
                              toast.success("Master document created");
                            } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't create master document"); }
                          }}
                          className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-[#2a2a2a] flex-shrink-0"
                          title="Add master document to this folder"
                          data-testid={`doc-folder-add-master-${f.folder_id}`}>
                            <span className="text-[9px] font-bold text-[#5A90F5] px-0.5">M+</span>
                          </button>
                        );
                      })()}
                      <button onClick={async (e) => { e.stopPropagation();
                        const inside = allDocs.filter((d) => d.parent_folder_id === f.folder_id);
                        if (inside.length > 0) return toast.error("Folder not empty");
                        await removeFolder(f.folder_id);
                      }}
                        className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-[#2a2a2a]" title="Delete folder (must be empty)">
                        <Trash2 className="w-3 h-3 text-[#FF3B30]" />
                      </button>
                    </div>
                    {expanded && depth < 2 && renderDocFolder(f.folder_id, depth + 1)}
                  </div>
                );
              });
              docsHere.forEach((d) => out.push(renderDocItem(d, depth)));
              return out;
            };
            return renderDocFolder(null, 0);
          })()}
        </div>
      ) : (
        <>

      {/* ADD NODE */}
      <div className="px-4 py-3 border-b border-[#2A2A2A]">
        <div
          draggable
          onDragStart={(e) => onDragStart(e, "blank")}
          onDragEnd={onDragEnd}
          onClick={() => window.dispatchEvent(new CustomEvent("crescendo:add-blank-now"))}
          className="w-full px-3 py-3 rounded-md border-2 border-dashed border-[#3A3A3A] hover:border-[#6A6A6A] hover:bg-[#A1A1AA]/10 text-[#A1A1AA] text-xs uppercase tracking-wider flex items-center justify-center gap-2 cursor-grab active:cursor-grabbing mb-2"
          title="Drag onto a time slot or click"
          data-testid="sidebar-add-blank"
        >
          <Plus className="w-3.5 h-3.5" /> Add Node
        </div>
      </div>

      {/* TEMPLATES — second from top (user request). */}
      <div className="px-4 py-3 border-b border-[#2A2A2A]">
        <div className="flex items-center justify-between mb-2">
          <button
            className="meta-label flex items-center gap-1 hover:text-[#EDEDED]"
            onClick={() => toggleSection("templates")}
            data-testid="toggle-templates"
          >
            <ChevronDown className={`w-3 h-3 transition-transform ${isCollapsed("templates") ? "-rotate-90" : ""}`} />
            Templates
          </button>
          <Popover>
            <PopoverTrigger asChild>
              <button className="p-1 rounded hover:bg-[#1a1a1a] text-[#A1A1AA] hover:text-[#EDEDED]" data-testid="new-template-plus-btn" aria-label="Add template folder">
                <Plus className="w-4 h-4" />
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="bg-[#141414] border-[#2A2A2A] text-[#EDEDED] p-1 w-52">
              <button onClick={() => setShowNewTplDialog(true)} className="w-full text-left px-2 py-1.5 rounded text-sm hover:bg-[#1a1a1a] flex items-center gap-2" data-testid="new-template-btn"><Plus className="w-3.5 h-3.5" /> New Template</button>
              <button onClick={() => addFolderAndRename("template")} className="w-full text-left px-2 py-1.5 rounded text-sm hover:bg-[#1a1a1a] flex items-center gap-2" data-testid="new-template-folder-btn"><FolderPlus className="w-3.5 h-3.5" /> New Folder</button>
            </PopoverContent>
          </Popover>
        </div>
        <div className={`space-y-0.5 ${isCollapsed("templates") ? "hidden" : ""}`}>
          {templates.map((t) => {
            const tplSelected = selectedTplIds.has(t.template_id);
            return (
            <div key={t.template_id}
              draggable
              onDragStart={(e) => { e.dataTransfer.setData("application/x-crescendo-template-id", t.template_id); e.dataTransfer.effectAllowed = "copy"; }}
              onClick={(e) => {
                if (e.shiftKey || e.ctrlKey || e.metaKey) {
                  e.preventDefault();
                  setSelectedTplIds((prev) => { const n = new Set(prev); n.has(t.template_id) ? n.delete(t.template_id) : n.add(t.template_id); return n; });
                  return;
                }
                setSelectedTplIds(new Set());
                window.dispatchEvent(new CustomEvent("crescendo:instantiate-template", { detail: t }));
              }}
              className={`group flex items-center gap-2 px-2 py-1 rounded cursor-pointer hover:bg-[#151515] ${tplSelected ? "bg-[#0055FF]/20 ring-1 ring-[#0055FF]" : ""}`}
              data-testid={`template-item-${t.template_id}`}
              title="Click To Paste At Now · Drag To A Day To Paste There · Shift+Click For Multi-Select"
            >
              <FileText className="w-3.5 h-3.5 text-[#A1A1AA]" />
              <span className="flex-1 text-xs truncate">{t.name}</span>
              {/* "+N node" suffix removed (user request). */}
              <button onClick={async (e) => { e.stopPropagation(); await removeTemplate(t.template_id); }}
                className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-[#2a2a2a]">
                <Trash2 className="w-3 h-3 text-[#FF3B30]" />
              </button>
            </div>
            );
          })}
        </div>
      </div>

      {/* UNSCHEDULED — tasks with no specific time, draggable to calendar. */}
      <UnscheduledPanel workspaceId={current?.workspace_id} />

      {/* CLASSES — Iter40: moved to RightPanel; hidden here.
          remains in case we need to swap it back, but is gated behind
          a constant feature flag so it doesn't render. */}
      {false && (
      <>
      <div className="px-4 py-3 border-b border-[#2A2A2A]">
        <div className="flex items-center justify-between mb-2">
          <button
            className="meta-label flex items-center gap-1 hover:text-[#EDEDED]"
            onClick={() => {
              // Iter39 user request: "If no classes exist, when the
              // user clicks the dropdown, it should not expand."
              // Behavior preserved, but per iter40: don't change the
              // visual color/opacity when empty.
              if (classes.length === 0) return;
              toggleSection("classes");
            }}
            onContextMenu={(e) => { e.preventDefault(); setSidebarCtx({ x: e.clientX, y: e.clientY, kind: "classes-section", item: null }); }}
            data-testid="toggle-classes"
            title={classes.length === 0 ? "Add a class first" : "Right-click to sort"}
            aria-disabled={classes.length === 0}
          >
            <ChevronDown className={`w-3 h-3 transition-transform ${(classes.length === 0 || isCollapsed("classes")) ? "-rotate-90" : ""}`} />
            Classes
          </button>
          <Popover>
            <PopoverTrigger asChild>
              <button className="p-1 rounded hover:bg-[#1a1a1a] text-[#A1A1AA] hover:text-[#EDEDED]" data-testid="new-class-plus-btn" aria-label="Add Class Or Folder">
                <Plus className="w-4 h-4" />
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="bg-[#141414] border-[#2A2A2A] text-[#EDEDED] p-1 w-44">
              <button onClick={openNewClass} className="w-full text-left px-2 py-1.5 rounded text-sm hover:bg-[#1a1a1a] flex items-center gap-2" data-testid="new-class-btn"><Plus className="w-3.5 h-3.5" /> New Class</button>
              <button onClick={() => addFolderAndRename("class")} className="w-full text-left px-2 py-1.5 rounded text-sm hover:bg-[#1a1a1a] flex items-center gap-2" data-testid="new-class-folder-btn"><FolderPlus className="w-3.5 h-3.5" /> New Folder</button>
            </PopoverContent>
          </Popover>
        </div>
        <div className={`space-y-0.5 ${classes.length === 0 ? "hidden" : "min-h-[80px]"} ${isCollapsed("classes") ? "hidden" : ""}`} data-testid="classes-list"
          onDragOver={(e) => { if (e.dataTransfer.types.includes("application/x-crescendo-class-id")) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; } }}
          onDrop={async (e) => {
            // Drop on the open class-list area outside any folder → unfolder
            // every class in the dragged batch (user bug: moving things out
            // of folders did not work).
            const id = e.dataTransfer.getData("application/x-crescendo-class-id");
            if (!id) return;
            e.preventDefault();
            const batch = e.dataTransfer.getData("application/x-crescendo-class-ids");
            const ids = batch ? batch.split(",").filter(Boolean) : [id];
            const any = ids.some((cid) => classes.find((c) => c.class_id === cid)?.parent_folder_id);
            if (!any) return;
            setClasses?.(classes.map((c) => ids.includes(c.class_id) ? { ...c, parent_folder_id: null } : c));
            await Promise.allSettled(ids.map((cid) => update(cid, { parent_folder_id: null })));
            setSelectedClsIds(new Set());
          }}
        >
          {/* Folders */}
          {folders.filter((f) => f.kind === "class").map((f) => {
            const children = classes.filter((c) => c.parent_folder_id === f.folder_id);
            return renderClassFolder(f, children);
          })}
          {classes.filter((c) => c.scope === "global" && !c.parent_folder_id).length > 0 && (
            <div
              className="class-scope-header"
              onContextMenu={(e) => { e.preventDefault(); setSidebarCtx({ x: e.clientX, y: e.clientY, kind: "classes-section", item: null }); }}
              title="Right-click to sort"
              data-testid="classes-global-header"
            >Global</div>
          )}
          {applyClassSort(classes.filter((c) => c.scope === "global" && !c.parent_folder_id)).map((cls) => renderClassRow(cls, false))}
          {classes.filter((c) => c.scope !== "global" && !c.parent_folder_id).length > 0 && (
            <div
              className="class-scope-header"
              onContextMenu={(e) => { e.preventDefault(); setSidebarCtx({ x: e.clientX, y: e.clientY, kind: "classes-section", item: null }); }}
              title="Right-click to sort"
              data-testid="classes-local-header"
            >Local</div>
          )}
          {applyClassSort(classes.filter((c) => c.scope !== "global" && !c.parent_folder_id)).map((cls) => renderClassRow(cls, false))}
          {/* Per user feedback: removed the explicit "Drop Here To Ungroup"
              row. Drag-out is now reliable through the parent class-list's
              own onDrop above (folders no longer e.stopPropagation on the
              dragover, and the folder.onDrop short-circuits when the
              dragged class is already in that same folder so the parent
              handler can finalise the unfolder). */}
        </div>
      </div>

      {/* TAGS — placed BELOW Classes per latest user request. */}
      <div className="px-4 py-3 border-b border-[#2A2A2A]">
        <button
          className="meta-label flex items-center gap-1 hover:text-[#EDEDED] mb-2"
          onClick={() => toggleSection("tags")}
          data-testid="toggle-tags"
        >
          <ChevronDown className={`w-3 h-3 transition-transform ${isCollapsed("tags") ? "-rotate-90" : ""}`} />
          Tags
        </button>
        <div className={`space-y-1 ${isCollapsed("tags") ? "hidden" : ""}`} data-testid="tags-list">
          {tags.map((t) => (
            <div key={t.tag_id} className="group flex items-center gap-2 px-1 py-1 rounded hover:bg-[#151515]" data-testid={`tag-item-${t.tag_id}`}>
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: t.color }} />
              <span className="flex-1 truncate text-xs text-[#EDEDED]">{t.name}</span>
              <button
                onClick={async () => {
                  const ok = await confirmDelete({ title: "Delete Tag?", message: `"${t.name}" will be removed from all events.`, confirmLabel: "Delete" });
                  if (ok) await removeTag(t.tag_id);
                }}
                className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-[#FF3B30]/15"
                data-testid={`tag-delete-${t.tag_id}`}
              >
                <Trash2 className="w-3 h-3 text-[#FF3B30]" />
              </button>
            </div>
          ))}
          <div className="flex items-center gap-1 mt-1">
            <input
              value={newTagName}
              onChange={(e) => setNewTagName(e.target.value)}
              onKeyDown={async (e) => {
                if (e.key === "Enter" && newTagName.trim()) {
                  e.preventDefault();
                  try { await createTag({ name: newTagName.trim() }); setNewTagName(""); } catch {}
                }
              }}
              placeholder="New Tag"
              className="flex-1 bg-[#0A0A0A] border border-[#2A2A2A] rounded px-1.5 py-1 outline-none focus:border-[#555] text-[11px] capitalize"
              data-testid="tag-new-name"
            />
            <button
              onClick={async () => { if (!newTagName.trim()) return; try { await createTag({ name: newTagName.trim() }); setNewTagName(""); } catch {} }}
              className="p-1 rounded hover:bg-[#1a1a1a] text-[#A1A1AA]"
              data-testid="tag-add-btn"
              title="Add tag"
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      {/* KEY / VALUE PAIRS — user-defined reusable variables that sit directly below Classes. */}
      <div className="px-4 py-3 border-b border-[#2A2A2A]">
        <button
          className="meta-label flex items-center gap-1 hover:text-[#EDEDED] mb-2"
          onClick={() => toggleSection("keypairs")}
          data-testid="toggle-keypairs"
        >
          <ChevronDown className={`w-3 h-3 transition-transform ${isCollapsed("keypairs") ? "-rotate-90" : ""}`} />
          Key Pairs
        </button>
        <div className={`space-y-1 ${isCollapsed("keypairs") ? "hidden" : ""}`} data-testid="keypairs-list">
          {keyPairs.length === 0 && null}
          {keyPairs.map((kp) => {
            // Type-aware preview — color shows a swatch, font shows the
            // value rendered in that font, etc.
            const t = kp.value_type || "string";
            return (
              <div key={kp.kp_id} className="group flex items-center gap-2 px-1 py-1 rounded hover:bg-[#151515]" data-testid={`keypair-item-${kp.kp_id}`}>
                <Key className="w-3 h-3 text-[#A1A1AA] flex-shrink-0" />
                <span className="text-xs font-mono text-[#5A90F5] truncate">{kp.key}</span>
                <span className="text-[#555] text-xs">=</span>
                {t === "color" ? (
                  <span className="flex items-center gap-1 flex-1 min-w-0">
                    <span className="w-3 h-3 rounded-sm flex-shrink-0 border border-[#2A2A2A]" style={{ background: kp.value }} />
                    <span className="text-[10px] font-mono text-[#A1A1AA] truncate">{kp.value}</span>
                  </span>
                ) : t === "font" ? (
                  <span className="text-xs text-[#EDEDED] truncate flex-1" style={{ fontFamily: kp.value }}>{kp.value}</span>
                ) : t === "boolean" ? (
                  <span className={`text-xs flex-1 ${String(kp.value).toLowerCase() === "true" ? "text-[#34C759]" : "text-[#FF3B30]"}`}>
                    {String(kp.value).toLowerCase() === "true" ? "true" : "false"}
                  </span>
                ) : (
                  <span className="text-xs text-[#EDEDED] truncate flex-1">{kp.value}</span>
                )}
                <span className="text-[#52525B] mr-1" title={t}>
                  {(() => { const I = KP_TYPE_ICON[t] || Type; return <I className="w-3 h-3" />; })()}
                </span>
                <button onClick={async () => {
                  const ok = await confirmDelete({ title: "Delete Key Pair?", message: `"${kp.key}" will be removed.`, confirmLabel: "Delete" });
                  if (ok) await removeKp(kp.kp_id);
                }}
                  className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-[#2a2a2a]">
                  <Trash2 className="w-3 h-3 text-[#FF3B30]" />
                </button>
              </div>
            );
          })}
          {/* Inline add row — iter35 user request: "For key-pair value
              data-types, it should be an icon, not text. The type
              drop-down should be nested inside of the value assignment
              box." Type chip is now nested inside the value input as a
              left-side adornment using a popover. */}
          <div className="flex items-center gap-1 mt-1">
            <input
              ref={kpKeyRef}
              value={newKpKey}
              onChange={(e) => setNewKpKey(e.target.value)}
              placeholder="key"
              className="w-16 bg-[#0A0A0A] border border-[#2A2A2A] rounded px-1.5 py-1 outline-none focus:border-[#555] text-[11px] font-mono"
              data-testid="keypair-new-key"
            />
            <span className="text-[#555] text-xs">=</span>
            <div className="flex-1 min-w-0 flex items-stretch bg-[#0A0A0A] border border-[#2A2A2A] rounded focus-within:border-[#555]">
              {newKpType === "color" ? (
                <input
                  type="color"
                  value={newKpVal || "#5A90F5"}
                  onChange={(e) => setNewKpVal(e.target.value)}
                  className="flex-1 h-full bg-transparent cursor-pointer outline-none"
                  data-testid="keypair-new-value"
                />
              ) : newKpType === "boolean" ? (
                <select
                  value={newKpVal || "true"}
                  onChange={(e) => setNewKpVal(e.target.value)}
                  className="flex-1 min-w-0 bg-transparent px-1.5 py-1 outline-none text-[11px]"
                  data-testid="keypair-new-value"
                >
                  <option value="true">true</option>
                  <option value="false">false</option>
                </select>
              ) : (
                <input
                  type={newKpType === "number" ? "number" : "text"}
                  value={newKpVal}
                  onChange={(e) => setNewKpVal(e.target.value)}
                  placeholder="value"
                  className="flex-1 min-w-0 bg-transparent px-1.5 py-1 outline-none text-[11px]"
                  data-testid="keypair-new-value"
                  onKeyDown={async (e) => {
                    if (e.key === "Enter" && newKpKey.trim()) {
                      e.preventDefault();
                      await createKp(newKpKey.trim(), newKpVal, newKpType);
                      setNewKpKey(""); setNewKpVal("");
                      setTimeout(() => { kpKeyRef.current?.focus(); }, 0);
                    }
                  }}
                />
              )}
              {/* Iter39 user request: "The type option for key-pairs
                  should be on the right" — moved from a left
                  adornment to a right-side icon button. */}
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="px-1.5 border-l border-[#2A2A2A] text-[#A1A1AA] hover:text-[#EDEDED] flex items-center"
                    data-testid="keypair-new-type"
                    title={`Type: ${newKpType}`}
                  >
                    {(() => {
                      const Icon = KP_TYPE_ICON[newKpType] || Type;
                      return <Icon className="w-3 h-3" />;
                    })()}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-32 p-1 bg-[#141414] border-[#2A2A2A]" align="end" sideOffset={4}>
                  {KP_TYPES.map((opt) => {
                    const Icon = KP_TYPE_ICON[opt.value];
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => { setNewKpType(opt.value); }}
                        className={`w-full flex items-center gap-1.5 px-2 py-1 rounded text-[11px] hover:bg-[#1a1a1a] ${newKpType === opt.value ? "text-[#5A90F5]" : "text-[#EDEDED]"}`}
                        data-testid={`keypair-type-opt-${opt.value}`}
                      >
                        <Icon className="w-3 h-3" /> {opt.label}
                      </button>
                    );
                  })}
                </PopoverContent>
              </Popover>
            </div>
            <button
              onClick={async () => {
                if (!newKpKey.trim()) return;
                await createKp(newKpKey.trim(), newKpVal, newKpType);
                setNewKpKey(""); setNewKpVal("");
                setTimeout(() => { kpKeyRef.current?.focus(); }, 0);
              }}
              className="p-1 rounded hover:bg-[#1a1a1a] text-[#A1A1AA]"
              data-testid="keypair-add-btn"
              title="Add key pair"
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      </>
      )}
      </>
      )}
      </div>

      {/* Iter39: Spotify Now-Playing widget — sticky at sidebar
          bottom, just above the user profile footer. Hidden when not
          connected. Persists through scroll. */}
      <SpotifyNowPlaying />

      {/* FOOTER: icon toolbar */}
      <div className="px-3 py-3 flex items-center gap-1" style={{ borderTop: "1px solid var(--cresc-border-sub)" }}>
        <Popover>
          <PopoverTrigger asChild>
            <button className="flex-1 flex items-center gap-2 px-2 py-1.5 rounded hover:bg-[#1a1a1a] text-left min-w-0" data-testid="user-menu">
              <div className="w-6 h-6 rounded-full bg-[#141414] border border-[#2A2A2A] flex items-center justify-center overflow-hidden flex-shrink-0">
                {user?.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> : <span className="text-[10px] font-medium">{(user?.username || user?.name)?.[0]?.toUpperCase()}</span>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold truncate flex items-center gap-1.5">
                  <span className="truncate">{user?.username || user?.name}</span>
                  {ent?.is_dev && (
                    <span
                      className="px-1.5 py-[1px] rounded-sm bg-[#5A90F5]/15 text-[#5A90F5] text-[8px] font-bold uppercase tracking-wider flex-shrink-0"
                      data-testid="dev-pill"
                      title="Dev mode — unlimited access"
                    >
                      DEV
                    </span>
                  )}
                </div>
                <div className="text-[10px] text-[#A1A1AA] truncate">{user?.email}</div>
              </div>
            </button>
          </PopoverTrigger>
          <PopoverContent side="top" align="start" className="bg-[#141414] border-[#2A2A2A] text-[#EDEDED] w-72 p-2">
            <div className="px-2 py-2 border-b border-[#2A2A2A]">
              <div className="text-sm font-semibold">{user?.username || user?.name}</div>
              <div className="text-xs text-[#A1A1AA]">{user?.email}</div>
            </div>
            {accounts.filter((a) => a.user_id !== user?.user_id).length > 0 && (
              <div className="py-1 border-b border-[#2A2A2A]">
                <div className="class-scope-header">Switch Account</div>
                {accounts.filter((a) => a.user_id !== user?.user_id).map((a) => (
                  <button key={a.user_id} onClick={() => switchAccount(a.user_id)}
                    className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-[#1a1a1a] text-left"
                    data-testid={`quick-switch-account-${a.user_id}`}
                  >
                    <div className="w-6 h-6 rounded-full bg-[#0A0A0A] border border-[#2A2A2A] overflow-hidden flex items-center justify-center flex-shrink-0">
                      {a.picture ? <img src={a.picture} alt="" className="w-full h-full object-cover" /> : <span className="text-[10px]">{(a.username || a.name)?.[0]?.toUpperCase()}</span>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs truncate">{a.username || a.name}</div>
                      <div className="text-[10px] text-[#A1A1AA] truncate">{a.email}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
            <button onClick={() => { setSettingsTab("account"); setSettingsOpen(true); }}
              className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-[#1a1a1a] text-sm" data-testid="manage-accounts-btn">
              <UserPlus className="w-4 h-4" /> Manage Accounts
            </button>
            {/* Iter41: "Library" link removed from this profile menu since
                it's now a top-level item in the main vertical nav above. */}
            {/* Admin Panel link — only visible for is_dev users (currently
                jamezcua@crescendo.dev). Iter32 user request: make /admin
                discoverable instead of URL-only. */}
            {ent?.is_dev && (
              <Link
                to="/admin"
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-[#1a1a1a] text-sm text-[#5A90F5]"
                data-testid="open-admin-panel"
              >
                <Sparkles className="w-4 h-4" /> Admin Panel
              </Link>
            )}

            {ent?.is_dev && (
              <Link to="/dev"
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm hover:bg-[#1a1a1a] text-[#A1A1AA] hover:text-[#EDEDED] transition-colors"
                data-testid="dev-lab-btn">
                <Zap className="w-4 h-4" /> Dev Lab
              </Link>
            )}
            {/* Business admin — visible for is_dev OR business plan owners. */}
            {ent?.is_business_member && !ent?.is_business_admin && (
              <Link
                to="/business"
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-[#1a1a1a] text-sm text-[#E0B355]"
                data-testid="open-business-account"
              >
                <Building2 className="w-4 h-4" /> Business Account
              </Link>
            )}
            {(ent?.is_dev || ent?.is_business_admin) && (
              <Link
                to="/business"
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-[#1a1a1a] text-sm text-[#E0B355]"
                data-testid="open-business-admin"
              >
                <Crown className="w-4 h-4" /> Business Admin
              </Link>
            )}

            <button onClick={logout}
              className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-[#1a1a1a] text-sm text-[#FF3B30]" data-testid="logout-btn">
              <LogOut className="w-4 h-4" /> Sign Out
            </button>
          </PopoverContent>
        </Popover>
        <button onClick={() => window.dispatchEvent(new CustomEvent("crescendo:open-history"))}
          className="p-2 rounded-lg transition-colors flex-shrink-0"
          style={{ color: "var(--cresc-text-3)" }}
          onMouseEnter={e => { e.currentTarget.style.background = "var(--cresc-surface-3)"; e.currentTarget.style.color = "var(--cresc-text-2)"; }}
          onMouseLeave={e => { e.currentTarget.style.background = ""; e.currentTarget.style.color = "var(--cresc-text-3)"; }}
          data-testid="sidebar-history-btn" aria-label="History">
          <History className="w-4 h-4" />
        </button>
        <button onClick={() => window.dispatchEvent(new CustomEvent("crescendo:open-theme"))}
          className="p-2 rounded-lg transition-colors flex-shrink-0"
          style={{ color: "var(--cresc-text-3)" }}
          onMouseEnter={e => { e.currentTarget.style.background = "var(--cresc-surface-3)"; e.currentTarget.style.color = "var(--cresc-text-2)"; }}
          onMouseLeave={e => { e.currentTarget.style.background = ""; e.currentTarget.style.color = "var(--cresc-text-3)"; }}
          aria-label="Theme">
          <Palette className="w-4 h-4" />
        </button>
        <button onClick={() => { setSettingsTab("appearance"); setSettingsOpen(true); }}
          className="p-2 rounded-lg transition-colors flex-shrink-0"
          style={{ color: "var(--cresc-text-3)" }}
          onMouseEnter={e => { e.currentTarget.style.background = "var(--cresc-surface-3)"; e.currentTarget.style.color = "var(--cresc-text-2)"; }}
          onMouseLeave={e => { e.currentTarget.style.background = ""; e.currentTarget.style.color = "var(--cresc-text-3)"; }}
          data-testid="settings-btn" aria-label="Settings">
          <SettingsIcon className="w-4 h-4" />
        </button>
      </div>

      <SettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} initialTab={settingsTab} />

      {/* Folder "delete all including contents" confirm dialog. */}
      {/* Workspace delete dialog — requires typing the workspace name. */}
      <Dialog open={!!wsDeleteTarget} onOpenChange={(v) => { if (!v) { setWsDeleteTarget(null); setWsDeleteTyped(""); } }}>
        <DialogContent className="bg-[#141414] border-[#2A2A2A] text-[#EDEDED] max-w-md" data-testid="ws-delete-dialog">
          <DialogHeader className="space-y-3">
            <DialogTitle className="font-display">Delete this workspace?</DialogTitle>
            <DialogDescription className="text-[#A1A1AA] pt-1">
              You are about to permanently remove "{wsDeleteTarget?.name}" and all of its nodes, events, and snapshots. Type the workspace name below to confirm.
            </DialogDescription>
          </DialogHeader>
          <input
            autoFocus
            value={wsDeleteTyped}
            onChange={(e) => setWsDeleteTyped(e.target.value)}
            placeholder={wsDeleteTarget?.name || ""}
            className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-md px-3 py-2 outline-none focus:border-[#FF3B30] text-sm"
            data-testid="ws-delete-typed"
          />
          <DialogFooter>
            <button onClick={() => { setWsDeleteTarget(null); setWsDeleteTyped(""); }}
              className="px-3 py-1.5 rounded-md border border-[#2A2A2A] text-xs">Cancel</button>
            <button
              disabled={!wsDeleteTarget || wsDeleteTyped.trim() !== (wsDeleteTarget?.name || "")}
              onClick={async () => {
                if (!wsDeleteTarget) return;
                await deleteWorkspace(wsDeleteTarget.workspace_id);
                setWsDeleteTarget(null); setWsDeleteTyped("");
              }}
              className="bg-[#FF3B30] text-white px-3 py-1.5 rounded-md text-xs font-medium disabled:opacity-50"
              data-testid="ws-delete-confirm-btn"
            >Delete workspace</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!folderNukeTarget} onOpenChange={(v) => { if (!v) { setFolderNukeTarget(null); setFolderNukeConfirm(false); } }}>
        <DialogContent className="bg-[#141414] border-[#2A2A2A] text-[#EDEDED] max-w-md" data-testid="folder-nuke-dialog">
          <DialogHeader className="space-y-3">
            <DialogTitle className="font-display">Delete folder and contents</DialogTitle>
            <DialogDescription className="text-[#A1A1AA] pt-1">
              This will permanently remove "{folderNukeTarget?.name}" AND everything inside it ({folderNukeTarget?.kind === "workspace" ? "workspaces" : "classes"}). This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <label className="flex items-center gap-3 py-3 text-xs cursor-pointer select-none">
            <input
              type="checkbox"
              checked={folderNukeConfirm}
              onChange={(e) => setFolderNukeConfirm(e.target.checked)}
              className="w-5 h-5 rounded border-2 border-[#2A2A2A] bg-[#0A0A0A] accent-[#FF3B30] cursor-pointer"
              data-testid="folder-nuke-confirm"
            />
            <span className="text-[#EDEDED]">I understand this will delete everything inside.</span>
          </label>
          <DialogFooter>
            <button
              onClick={() => { setFolderNukeTarget(null); setFolderNukeConfirm(false); }}
              className="px-3 py-1.5 rounded-md border border-[#2A2A2A] text-xs"
              data-testid="folder-nuke-cancel"
            >Cancel</button>
            <button
              disabled={!folderNukeConfirm}
              onClick={async () => {
                if (!folderNukeTarget || !folderNukeConfirm) return;
                const target = folderNukeTarget;
                if (target.kind === "workspace") {
                  const kids = workspaces.filter((w) => w.parent_folder_id === target.folder_id);
                  for (const w of kids) {
                    try { await api.delete(`/workspaces/${w.workspace_id}`); } catch {}
                  }
                } else {
                  const kids = classes.filter((c) => c.parent_folder_id === target.folder_id);
                  for (const c of kids) {
                    try { await removeCls(c.class_id); } catch {}
                  }
                }
                await removeFolder(target.folder_id);
                setFolderNukeTarget(null); setFolderNukeConfirm(false);
              }}
              className="bg-[#FF3B30] text-white px-3 py-1.5 rounded-md text-xs font-medium disabled:opacity-50"
              data-testid="folder-nuke-confirm-btn"
            >Delete Folder + Contents</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Class dialog */}
      <Dialog open={classDialogOpen} onOpenChange={setClassDialogOpen}>
        <DialogContent className="bg-[#141414] border-[#2A2A2A] text-[#EDEDED]">
          <DialogHeader>
            <DialogTitle className="font-display">{editingClass ? "Edit Class" : "New Class"}</DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">Group nodes by class with a color and description.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="meta-label block mb-1">NAME</label>
              <input value={cName} onChange={(e) => setCName(e.target.value)} placeholder="e.g. chores"
                className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-md px-3 py-2 outline-none focus:border-[#555]"
                data-testid="class-name-input" />
            </div>
            <div>
              <label className="meta-label block mb-1">COLOR</label>
              <div className="flex gap-2 items-center flex-wrap">
                {PRESET_COLORS.map((c) => (
                  <button key={c} onClick={() => setCColor(c)}
                    className={`w-7 h-7 rounded-full border-2 ${cColor === c ? "border-[#EDEDED]" : "border-transparent"}`}
                    style={{ background: c }} />
                ))}
                <label className="relative w-7 h-7 rounded-full cursor-pointer overflow-hidden border-2 border-dashed border-[#3A3A3A] flex items-center justify-center">
                  <input type="color" value={cColor} onChange={(e) => setCColor(e.target.value)}
                    className="absolute inset-0 opacity-0 cursor-pointer" />
                  <div className="w-4 h-4 rounded-full" style={{ background: cColor }} />
                </label>
                <span className="text-xs text-[#A1A1AA]">{cColor}</span>
              </div>
            </div>
            <div>
              <label className="meta-label block mb-1">SCOPE</label>
              <div className="flex gap-2">
                {[["global","Global (all workspaces)"], ["local","Local (this workspace)"]].map(([v, l]) => (
                  <button key={v} onClick={() => setCScope(v)}
                    className={`flex-1 text-xs py-2 rounded border flex items-center justify-center gap-1 ${cScope === v ? "border-[#EDEDED] bg-[#1a1a1a]" : "border-[#2A2A2A] text-[#A1A1AA]"}`}
                    data-testid={`class-scope-${v}`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="meta-label block mb-1">DESCRIPTION</label>
              <textarea value={cDesc} onChange={(e) => setCDesc(e.target.value)} rows={3}
                placeholder="What does this class represent?"
                className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-md px-3 py-2 outline-none focus:border-[#555] text-sm"
                data-testid="class-desc-input" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            {editingClass && (
              <button onClick={deleteClass} className="px-3 py-2 text-sm text-[#FF3B30] hover:bg-[#FF3B30]/10 rounded-md">Delete</button>
            )}
            <button onClick={saveClass} className="bg-[#EDEDED] text-[#0A0A0A] px-4 py-2 rounded-md font-medium hover:bg-white" data-testid="class-save-btn">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sidebar right-click context menu — rendered via portal so it escapes
          any stacking context and always appears above the documents view. */}
      {sidebarCtx && createPortal(
        <div className="sidebar-ctx" style={{ left: sidebarCtx.x, top: sidebarCtx.y }} onClick={(e) => e.stopPropagation()} data-testid="sidebar-ctx-menu">
          {sidebarCtx.kind === "folder" ? (
            <>
              <div className="ctx-sub">Folder</div>
              <div className="ctx-item" onClick={() => {
                setRenameTarget({ kind: "folder", id: sidebarCtx.item.folder_id, value: sidebarCtx.item.name });
                setTimeout(() => renameInputRef.current?.focus(), 10);
                setSidebarCtx(null);
              }} data-testid="sctx-folder-rename">
                <Edit3 className="w-3.5 h-3.5" /> Rename
              </div>
              <div className="ctx-sub">Color</div>
              <div className="flex flex-wrap gap-1.5 px-2 py-1.5">
                {[null, ...PRESET_COLORS].map((c, idx) => (
                  <button key={c || `none-${idx}`}
                    onClick={async () => { await updateFolder(sidebarCtx.item.folder_id, { color: c }); setSidebarCtx(null); }}
                    className={`w-5 h-5 rounded-full border ${sidebarCtx.item.color === c ? "ring-2 ring-[#EDEDED]" : ""}`}
                    style={{ background: c || "transparent", borderColor: c || "#2A2A2A" }}
                    data-testid={`sctx-folder-color-${c || "clear"}`}
                    title={c || "Clear"}
                  />
                ))}
              </div>
              <div className="ctx-sub">Sort By</div>
              {FOLDER_SORTS.map((s) => (
                <div key={s.key} className="ctx-item" onClick={async () => { await updateFolder(sidebarCtx.item.folder_id, { sort_by: s.key }); setSidebarCtx(null); }}
                  data-testid={`sctx-folder-sort-${s.key}`}
                >
                  <span className="w-3.5 h-3.5 inline-flex items-center justify-center">
                    {sidebarCtx.item.sort_by === s.key ? <Check className="w-3 h-3" /> : null}
                  </span>
                  {s.label}
                </div>
              ))}
              <div className="ctx-sep" />
              <div className="ctx-item text-[#FF3B30] hover:!bg-[#FF3B30]/15" onClick={async () => {
                const hasChildren = sidebarCtx.item.kind === "workspace"
                  ? workspaces.some((w) => w.parent_folder_id === sidebarCtx.item.folder_id)
                  : classes.some((c) => c.parent_folder_id === sidebarCtx.item.folder_id);
                if (hasChildren) { setSidebarCtx(null); return; }
                const ok = await confirmDelete({ title: "Delete Folder?", message: `"${sidebarCtx.item.name}" will be permanently removed.`, confirmLabel: "Delete", checkboxLabel: "I understand this is permanent" });
                if (!ok) { setSidebarCtx(null); return; }
                await removeFolder(sidebarCtx.item.folder_id);
                setSidebarCtx(null);
              }} data-testid="sctx-folder-delete">
                <Trash2 className="w-3.5 h-3.5" /> Delete Empty Folder
              </div>
              <div className="ctx-item text-[#FF3B30] hover:!bg-[#FF3B30]/15" onClick={() => {
                setFolderNukeTarget(sidebarCtx.item);
                setSidebarCtx(null);
              }} data-testid="sctx-folder-delete-all">
                <Trash2 className="w-3.5 h-3.5" /> Delete All Including Contents
              </div>
            </>
          ) : sidebarCtx.kind === "workspace" ? (
            <>
              <div className="ctx-sub">Workspace</div>
              <div className="ctx-item" onClick={() => {
                setRenameTarget({ kind: "workspace", id: sidebarCtx.item.workspace_id, value: sidebarCtx.item.name });
                setTimeout(() => renameInputRef.current?.focus(), 10);
                setSidebarCtx(null);
              }} data-testid="sctx-ws-rename"><Edit3 className="w-3.5 h-3.5" /> Rename</div>
              <div className="ctx-item" onClick={() => { openEditWs(sidebarCtx.item); setSidebarCtx(null); }}><FileEdit className="w-3.5 h-3.5" /> Edit Details…</div>
              <div className="ctx-item" onClick={() => { setShareTarget(sidebarCtx.item); setSidebarCtx(null); }} data-testid="sctx-ws-share">
                <Globe className="w-3.5 h-3.5" /> Share…
              </div>
              <div className="ctx-item" onClick={() => {
                // Move this workspace to position 0 (top)
                const sorted = [...workspaces].sort((a, b) => (a.position || 0) - (b.position || 0));
                const idx = sorted.findIndex(w => w.workspace_id === sidebarCtx.item.workspace_id);
                if (idx > 0) reorderWorkspaces(sidebarCtx.item.workspace_id, sorted[0].workspace_id, false);
                setSidebarCtx(null);
              }}><ArrowUp className="w-3.5 h-3.5" /> Move to top</div>
              <div className="ctx-sep" />
              {workspaces.length > 1 && (
                <div className="ctx-item text-[#FF3B30]" onClick={() => { setWsDeleteTarget(sidebarCtx.item); setSidebarCtx(null); }}
                  data-testid="sctx-ws-delete"><Trash2 className="w-3.5 h-3.5" /> Delete workspace…</div>
              )}
            </>
          ) : sidebarCtx.kind === "doc" ? (
            <>
              <div className="ctx-sub">Document</div>
              <div className="ctx-item" onClick={() => {
                setRenameTarget({ kind: "doc", id: sidebarCtx.item.doc_id, value: sidebarCtx.item.name });
                setTimeout(() => renameInputRef.current?.focus(), 10);
                setSidebarCtx(null);
              }} data-testid="sctx-doc-rename"><Edit3 className="w-3.5 h-3.5" /> Rename</div>
              <div className="ctx-item" onClick={async () => {
                // Add a Google-Docs-style sub-tab to this document (user
                // request).
                const d = sidebarCtx.item;
                const tabs = Array.isArray(d.tabs) ? d.tabs : [];
                const tab = { tab_id: `tab_${Math.random().toString(36).slice(2, 10)}`, name: `Tab ${tabs.length + 1}`, body: "" };
                try {
                  await updateDoc(d.doc_id, {
                    name: d.name, kind: d.kind, body: d.body || "",
                    mime_type: d.mime_type, workspace_id: d.workspace_id,
                    parent_folder_id: d.parent_folder_id, tabs: [...tabs, tab],
                  });
                  reloadDocs?.();
                  /* silent: tab added */
                } catch { toast.error("Couldn't add tab"); }
                setSidebarCtx(null);
              }} data-testid="sctx-doc-add-tab">
                <Plus className="w-3.5 h-3.5" /> Add tab
              </div>
              <div
                className="ctx-item"
                onClick={async () => {
                  // Authenticated download — endpoint requires bearer
                  // token (the prior <a href> blew up with 401 because
                  // a new-tab navigation has no Authorization header).
                  // We fetch via the api client, blob the response,
                  // then trigger a same-tab download via an anchor.
                  const docId = sidebarCtx.item.doc_id;
                  const docName = sidebarCtx.item.name || "document";
                  setSidebarCtx(null);
                  try {
                    const res = await api.get(`/documents/${docId}/download`, { responseType: "blob" });
                    const url = URL.createObjectURL(res.data);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `${docName}.html`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                  } catch (e) {
                    window.alert("Download failed. Please try again.");
                  }
                }}
                data-testid="sctx-doc-download"
              >
                <Download className="w-3.5 h-3.5" /> Download
              </div>
              <div className="ctx-item" onClick={async () => {
                const pw = window.prompt("Set a password for this document (leave blank to remove):");
                if (pw === null) { setSidebarCtx(null); return; }
                try {
                  await api.put(`/documents/${sidebarCtx.item.doc_id}/password`, { password: pw.trim() });
                  toast.success(pw.trim() ? "Password set" : "Password removed");
                } catch { toast.error("Couldn't set password"); }
                setSidebarCtx(null);
              }} data-testid="sctx-doc-password">
                <Key className="w-3.5 h-3.5" /> {sidebarCtx.item.doc_password_hash ? "Change Password…" : "Set Password…"}
              </div>
              {sidebarCtx.item.parent_folder_id && (
                <div className="ctx-item" onClick={async () => {
                  const folderId = sidebarCtx.item.parent_folder_id;
                  const folderName = allDocs.find(d => d.parent_folder_id === folderId)?.name || "folder";
                  setSidebarCtx(null);
                  try {
                    const res = await api.get(`/folders/${folderId}/export-zip`, { responseType: "blob" });
                    const url = URL.createObjectURL(res.data);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `${folderName}.zip`;
                    document.body.appendChild(a); a.click(); document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                  } catch { toast.error("Export failed"); }
                }} data-testid="sctx-doc-export-folder">
                  <Download className="w-3.5 h-3.5" /> Export Folder as ZIP
                </div>
              )}
              <div className="ctx-sub">Class</div>
              <div className="flex flex-wrap gap-1.5 px-2 py-1.5 max-h-32 overflow-y-auto">
                <button
                  type="button"
                  onClick={async () => {
                    await updateDoc(sidebarCtx.item.doc_id, {
                      name: sidebarCtx.item.name,
                      kind: sidebarCtx.item.kind,
                      body: sidebarCtx.item.body || "",
                      mime_type: sidebarCtx.item.mime_type || null,
                      workspace_id: sidebarCtx.item.workspace_id || null,
                      parent_folder_id: sidebarCtx.item.parent_folder_id || null,
                      class_id: null,
                    });
                    setSidebarCtx(null);
                  }}
                  className={`px-2 py-0.5 text-[10px] rounded border ${!sidebarCtx.item.class_id ? "border-[#5A90F5] text-[#5A90F5]" : "border-[#2A2A2A] text-[#A1A1AA] hover:border-[#5A90F5]"}`}
                  data-testid="sctx-doc-class-none"
                >None</button>
                {classes.map((c) => (
                  <button
                    key={c.class_id}
                    type="button"
                    onClick={async () => {
                      await updateDoc(sidebarCtx.item.doc_id, {
                        name: sidebarCtx.item.name,
                        kind: sidebarCtx.item.kind,
                        body: sidebarCtx.item.body || "",
                        mime_type: sidebarCtx.item.mime_type || null,
                        workspace_id: sidebarCtx.item.workspace_id || null,
                        parent_folder_id: sidebarCtx.item.parent_folder_id || null,
                        class_id: c.class_id,
                      });
                      setSidebarCtx(null);
                    }}
                    className={`flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded border ${sidebarCtx.item.class_id === c.class_id ? "ring-2 ring-[#5A90F5] border-[#5A90F5]" : "border-[#2A2A2A] hover:border-[#5A90F5]"}`}
                    title={c.name}
                    data-testid={`sctx-doc-class-${c.class_id}`}
                  >
                    <span className="w-2 h-2 rounded-full" style={{ background: c.color }} />
                    <span className="text-[#EDEDED] max-w-[80px] truncate">{c.name}</span>
                  </button>
                ))}
              </div>
              <div className="ctx-sep" />
              <div className="ctx-item text-[#FF3B30] hover:!bg-[#FF3B30]/15" onClick={async () => {
                const ok = await confirmDelete({
                  title: "Delete Document?",
                  message: `"${sidebarCtx.item.name}" will be permanently removed.`,
                  confirmLabel: "Delete",
                  checkboxLabel: "I understand this also removes all sub-tabs and content.",
                });
                if (!ok) { setSidebarCtx(null); return; }
                await removeDoc(sidebarCtx.item.doc_id);
                if (activeDocId === sidebarCtx.item.doc_id) setActiveDocId(null);
                setSidebarCtx(null);
              }}
                data-testid="sctx-doc-delete"><Trash2 className="w-3.5 h-3.5" /> Delete</div>
            </>
          ) : sidebarCtx.kind === "doc-folder" ? (
            <>
              <div className="ctx-sub">Folder</div>
              <div className="ctx-item" onClick={() => {
                setRenameTarget({ kind: "folder", id: sidebarCtx.item.folder_id, value: sidebarCtx.item.name });
                setTimeout(() => renameInputRef.current?.focus(), 10);
                setSidebarCtx(null);
              }}><Edit3 className="w-3.5 h-3.5" /> Rename</div>
              <div className="ctx-item" style={{ color: "#5A90F5" }} onClick={async () => {
                try {
                  const ws = current?.workspace_id;
                  if (!ws) { toast.error("No active workspace"); return; }
                  await createDoc({ name: "Master Document", kind: "master", body: "", workspace_id: ws, parent_folder_id: sidebarCtx.item.folder_id });
                  reloadDocs?.();
                  toast.success("Master document created");
                } catch { toast.error("Couldn't create master document"); }
                setSidebarCtx(null);
              }} data-testid="sctx-doc-folder-new-master">
                <FileText className="w-3.5 h-3.5" /> New Master Document
              </div>
              <div className="ctx-item" onClick={async () => {
                try {
                  const ws = current?.workspace_id;
                  if (!ws) return;
                  await createDoc({ name: "Untitled", kind: "markdown", body: "", workspace_id: ws, parent_folder_id: sidebarCtx.item.folder_id });
                  reloadDocs?.();
                } catch { toast.error("Couldn't create document"); }
                setSidebarCtx(null);
              }}><FileText className="w-3.5 h-3.5" /> New Document</div>
              <div className="ctx-sep" />
              <div className="ctx-item text-[#FF3B30] hover:!bg-[#FF3B30]/15" onClick={async () => {
                const ok = await confirmDelete({ title: "Delete Folder?", message: `"${sidebarCtx.item.name}" and all its documents will be permanently removed.`, confirmLabel: "Delete Folder", checkboxLabel: "I understand all contents will be deleted" });
                if (!ok) { setSidebarCtx(null); return; }
                try { await removeFolder(sidebarCtx.item.folder_id); reloadDocs?.(); } catch { toast.error("Failed to delete folder"); }
                setSidebarCtx(null);
              }}><Trash2 className="w-3.5 h-3.5" /> Delete Folder</div>
            </>
          ) : sidebarCtx.kind === "doc-tab" ? (
            <>
              <div className="ctx-sub">Tab</div>
              <div className="ctx-item" onClick={() => {
                setRenameTarget({ kind: "tab", id: sidebarCtx.item.tab_id, doc_id: sidebarCtx.item.doc_id, value: sidebarCtx.item.name });
                setSidebarCtx(null);
              }} data-testid="sctx-tab-rename"><Edit3 className="w-3.5 h-3.5" /> Rename</div>
              <div className="ctx-sep" />
              <div className="ctx-item text-[#FF3B30] hover:!bg-[#FF3B30]/15" onClick={async () => {
                const ok = await confirmDelete({ title: "Delete Tab?", message: `"${sidebarCtx.item.name}" and its content will be removed.`, confirmLabel: "Delete", checkboxLabel: "I understand this will remove all tab content" });
                if (!ok) { setSidebarCtx(null); return; }
                try {
                  const parent = allDocs.find((x) => x.doc_id === sidebarCtx.item.doc_id);
                  if (parent) {
                    const tabs = (parent.tabs || []).filter((x) => x.tab_id !== sidebarCtx.item.tab_id);
                    await updateDoc(parent.doc_id, {
                      name: parent.name, kind: parent.kind, body: parent.body || "",
                      mime_type: parent.mime_type, workspace_id: parent.workspace_id,
                      parent_folder_id: parent.parent_folder_id, tabs,
                    });
                    reloadDocs?.();
                  }
                } catch {}
                setSidebarCtx(null);
              }} data-testid="sctx-tab-delete"><Trash2 className="w-3.5 h-3.5" /> Delete Tab</div>
            </>
          ) : sidebarCtx.kind === "classes-section" ? (
            <>
              <div className="ctx-sub">Sort Classes</div>
              {FOLDER_SORTS.map((s) => (
                <div key={s.key} className="ctx-item" onClick={() => { setClassSort(s.key); setSidebarCtx(null); }}
                  data-testid={`sctx-classes-sort-${s.key}`}
                >
                  <span className="w-3.5 h-3.5 inline-flex items-center justify-center">
                    {classSort === s.key ? <Check className="w-3 h-3" /> : null}
                  </span>
                  {s.label}
                </div>
              ))}
            </>
          ) : (
            <>
              <div className="ctx-sub">Class</div>
              <div className="ctx-item" onClick={() => {
                setRenameTarget({ kind: "class", id: sidebarCtx.item.class_id, value: sidebarCtx.item.name });
                setTimeout(() => renameInputRef.current?.focus(), 10);
                setSidebarCtx(null);
              }} data-testid="sctx-cls-rename"><Edit3 className="w-3.5 h-3.5" /> Rename</div>
              <div className="ctx-item" onClick={() => { openEditClass(sidebarCtx.item); setSidebarCtx(null); }}><FileEdit className="w-3.5 h-3.5" /> Edit Details…</div>
              <div className="ctx-sub">Color</div>
              <div className="flex flex-wrap gap-1.5 px-2 py-1.5">
                {PRESET_COLORS.map((c) => (
                  <button key={c}
                    onClick={async () => { await update(sidebarCtx.item.class_id, { color: c }); setSidebarCtx(null); }}
                    className={`w-5 h-5 rounded-full ${sidebarCtx.item.color === c ? "ring-2 ring-[#EDEDED]" : ""}`}
                    style={{ background: c }}
                    data-testid={`sctx-cls-color-${c}`}
                  />
                ))}
              </div>
              <div className="ctx-sep" />
              <div className="ctx-item text-[#FF3B30]" onClick={async () => {
                const ok = await confirmDelete({ title: "Delete Class?", message: `"${sidebarCtx.item.name}" will be removed and unassigned from all events.`, confirmLabel: "Delete", checkboxLabel: "I understand this cannot be undone" });
                if (!ok) { setSidebarCtx(null); return; }
                await removeCls(sidebarCtx.item.class_id);
                setSidebarCtx(null);
              }} data-testid="sctx-cls-delete"><Trash2 className="w-3.5 h-3.5" /> Delete</div>
            </>
          )}
        </div>
      , document.body)}
    </aside>
  );
}

// ---------- Sharing dialog body ----------
function ShareDialogBody({ workspace, mode, setMode, invitees, setInvitees, password, setPassword, onClose }) {
  const [saving, setSaving] = useState(false);
  const [shareUrl, setShareUrl] = useState(() => workspace.share_token ? `${window.location.origin}/share/${workspace.share_token}` : "");
  const [showPwField, setShowPwField] = useState(!workspace.share_password_hash);
  // Outlook-style invitees: list of {email, role}.
  const initialInvitees = (() => {
    if (!invitees) return [];
    return invitees.split(/[, \n]+/).map((s) => s.trim()).filter(Boolean).map((e) => ({ email: e, role: "viewer" }));
  })();
  const [invitedList, setInvitedList] = useState(initialInvitees);
  const [pillDraft, setPillDraft] = useState("");

  const addPill = (raw) => {
    const v = (raw || pillDraft).trim().replace(/[, ]+$/, "");
    if (!v || !/.@./.test(v)) return;
    if (invitedList.find((x) => x.email.toLowerCase() === v.toLowerCase())) { setPillDraft(""); return; }
    setInvitedList((arr) => [...arr, { email: v, role: "viewer" }]);
    setPillDraft("");
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = { mode };
      if (mode === "invite" || mode === "secured") {
        body.invitees = invitedList.map((x) => x.email);
        // (Roles are stored in invitees email list verbatim today; full
        // viewer/editor enforcement on the backend is a follow-up — call
        // out in the PRD.)
      }
      if (mode === "secured" && (showPwField || password)) {
        if (!password) { toast.error("Password required"); setSaving(false); return; }
        body.password = password;
      }
      const { data } = await api.put(`/workspaces/${workspace.workspace_id}/share`, body);
      if (data.share_token) {
        setShareUrl(`${window.location.origin}/share/${data.share_token}`);
      } else {
        setShareUrl("");
      }
      toast.success("Share settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    }
    setSaving(false);
  };

  const MODES = [
    { key: "private",  label: "Private",  desc: "Only you" },
    { key: "public",   label: "Public",   desc: "Anyone with the link" },
    { key: "invite",   label: "Invite",   desc: "Listed people only" },
    // user request: rename "secured" → "secure" in UI (backend key kept).
    { key: "secured",  label: "Secure",   desc: "Invite + password" },
  ];
  const activeMode = MODES.find((m) => m.key === mode) || MODES[0];

  return (
    <div className="space-y-4 text-sm">
      {/* Mode dropdown — was a row of cards, now a single select (user
          request: "Let's make the share a dropdown instead of boxes"). */}
      <div>
        <label className="meta-label block mb-1">ACCESS</label>
        <ThemedSelect
          value={mode}
          onChange={(v) => setMode(v)}
          options={MODES.map((m) => ({ value: m.key, label: `${m.label} — ${m.desc}` }))}
          className="w-full"
          data-testid="share-mode-select"
        />
        <div className="text-[10px] text-[#A1A1AA] mt-1">{activeMode.desc}</div>
      </div>

      {(mode === "invite" || mode === "secured") && (
        <div>
          <label className="meta-label block mb-1">INVITED PEOPLE</label>
          {/* Outlook-style pill input + role dropdown per user (user
              request). */}
          <div
            className="flex flex-wrap items-center gap-1.5 px-2 py-1.5 bg-[#0A0A0A] border border-[#2A2A2A] rounded-md min-h-[40px] focus-within:border-[#5A90F5]"
            data-testid="share-invitees-input"
          >
            {invitedList.map((it, idx) => (
              <span
                key={`${it.email}-${idx}`}
                className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full bg-[#1F2937] border border-[#2A2A2A] text-[11px]"
                data-testid={`share-invitee-pill-${idx}`}
              >
                <span className="text-[#EDEDED]">{it.email}</span>
                <select
                  value={it.role}
                  onChange={(e) => setInvitedList((arr) => arr.map((x, i) => i === idx ? { ...x, role: e.target.value } : x))}
                  className="bg-transparent text-[10px] uppercase tracking-wider text-[#5A90F5] outline-none cursor-pointer"
                  data-testid={`share-invitee-role-${idx}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <option value="viewer">Viewer</option>
                  <option value="editor">Editor</option>
                </select>
                <button
                  onClick={() => setInvitedList((arr) => arr.filter((_, i) => i !== idx))}
                  className="px-1 rounded hover:bg-[#2a2a2a] text-[#A1A1AA]"
                  data-testid={`share-invitee-remove-${idx}`}
                  title="Remove"
                >
                  ×
                </button>
              </span>
            ))}
            <input
              value={pillDraft}
              onChange={(e) => setPillDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === "," || e.key === "Tab") {
                  if (pillDraft.trim()) { e.preventDefault(); addPill(); }
                }
                if (e.key === "Backspace" && !pillDraft && invitedList.length > 0) {
                  setInvitedList((arr) => arr.slice(0, -1));
                }
              }}
              onBlur={() => pillDraft && addPill()}
              placeholder={invitedList.length === 0 ? "name@example.com — Enter or comma to add" : ""}
              className="flex-1 min-w-[140px] bg-transparent outline-none text-[12px] text-[#EDEDED]"
              data-testid="share-invitee-draft"
            />
          </div>
          {/* Hidden field — keeps the legacy `invitees` parent-state in
              sync so save() pulls a comma-string if needed. */}
          <input type="hidden" value={invitedList.map((x) => x.email).join(", ")} onChange={() => setInvitees(invitedList.map((x) => x.email).join(", "))} />
        </div>
      )}

      {mode === "secured" && (
        <div>
          <label className="meta-label block mb-1">PASSWORD</label>
          {workspace.share_password_hash && !showPwField ? (
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-[#A1A1AA]">A password is currently set.</span>
              <button onClick={() => setShowPwField(true)} className="text-[11px] underline text-[#5A90F5]">Change</button>
            </div>
          ) : (
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Set a password"
              className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-md px-3 py-2 outline-none focus:border-[#555] text-xs font-mono"
              data-testid="share-password"
            />
          )}
        </div>
      )}

      {/* Share link — always shown for non-private modes; shows a placeholder
          before save so users see the eventual format (user request). */}
      {mode !== "private" && (
        <div className="rounded-md border border-[#2A2A2A] bg-[#0A0A0A] p-2.5 space-y-1.5">
          <div className="meta-label">SHARE LINK</div>
          <div className="flex items-center gap-2">
            <input
              readOnly
              value={shareUrl || `${window.location.origin}/share/…`}
              className="flex-1 bg-transparent text-[11px] font-mono outline-none text-[#EDEDED]"
              data-testid="share-link-input"
            />
            <button
              onClick={async () => { if (shareUrl) { const ok = await copyToClipboard(shareUrl); toast[ok?"success":"error"](ok?"Copied!":"Copy failed — try manually selecting the link"); } else { toast.message("Click Save first to generate the share link"); } }}
              className="px-3 py-1 rounded bg-white text-[#0A0A0A] text-[11px] font-semibold hover:bg-[#E7ECFA]"
              data-testid="share-copy-btn"
            >Copy</button>
          </div>
        </div>
      )}

      <div className="flex items-center justify-end gap-2 pt-1">
        <button onClick={onClose} className="px-3 py-1.5 rounded text-xs border border-[#2A2A2A] hover:bg-[#1a1a1a]" data-testid="share-cancel">Close</button>
        <button onClick={save} disabled={saving} className="px-3 py-1.5 rounded text-xs bg-[#5A90F5] text-[#0A0A0A] font-semibold hover:bg-[#7AA5F8] disabled:opacity-60" data-testid="share-save">{saving ? "Saving…" : "Save"}</button>
      </div>
    </div>
  );
}
